import { useEffect, useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  Lasso,
  Pentagon,
  Wand2,
  Sparkles,
  Crop,
  RotateCcw,
  RotateCw,
  FlipHorizontal2,
  FlipVertical2,
  Move3D,
  HelpCircle,
} from 'lucide-react';
import type {
  EditorTool,
  BrushSettings,
  TextSettings,
  ShapeSettings,
  MarqueeSettings,
  MarqueeShape,
  RetouchSettings,
  RetouchSettingsPatch,
  PenSettings,
  AnchorType,
  ShapeType,
  AIStatus,
  AICapability,
  InpaintEngine,
  ImageToImageEngine,
  RemoveBackgroundEngine,
  ReplaceBackgroundEngine,
  ReplaceBackgroundLightDirection,
  ReplaceBackgroundRecraftStyle,
  UpscaleMode,
  UpscaleEngine,
  UpscaleFactor,
  ControlMode,
  EditorOutputMode,
  SmartCreateMode,
  SmartCreateInsertModel,
  SliceRect,
} from '../types';
import { ReferenceImageDrop } from './ReferenceImageDrop';
import { ColorSwatchPopover, type ColorValue } from './ColorSwatchPopover';
import { RetouchPanel } from './RetouchPanel';
import { PenPanel } from './PenPanel';
import { TextPanel } from './TextPanel';
import { EditPathPanel } from './EditPathPanel';
import { AdjustmentsPicker, type AdjustmentKind } from './adjustments';
import { FilterPicker } from './filters';
import { SlicePanel } from './SlicePanel';
import type { ShapeFillValue } from '../types';
// Pull the labels + type from `path-ops.types` (not `path-ops`)
// so PropertiesPanel.tsx doesn't drag the paper.js runtime into
// any test that mounts it — paper crashes on import in happy-dom.
import { BOOLEAN_OP_LABELS, type BooleanOp } from '../lib/path-ops.types';

/** Adapt a `ShapeSettings.fill` (string | ShapeFillValue) to the
 *  ColorValue shape ColorSwatchPopover wants. The two are
 *  isomorphic; the conversion exists so the canvas-facing type
 *  doesn't have to import UI types. */
function shapeFillToColorValue(fill: string | ShapeFillValue): ColorValue {
  if (typeof fill === 'string') {
    // 'transparent' isn't a valid hex; show white in the popover so
    // the picker has a sensible starting point. The "No fill"
    // toggle next to the swatch keeps transparent as a separate
    // affordance.
    return { type: 'solid', color: fill === 'transparent' ? '#ffffff' : fill };
  }
  return fill;
}

function colorValueToShapeFill(v: ColorValue): ShapeFillValue {
  return v;
}
import { SelectionOpsPanel } from './SelectionOpsPanel';

export type PropertiesPanelPlacement = 'right' | 'bottom';

/** Live Fabric matrix shape consumed by the TransformPanel. Reads
 *  resolved (post-scale) `width`/`height` rather than raw scaleX/Y
 *  so users see real pixel dimensions in the panel — the
 *  `setTransformMatrix` writer translates back. */
export interface TransformMatrix {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  skewX: number;
  skewY: number;
}

interface PropertiesPanelProps {
  isOpen: boolean;
  placement: PropertiesPanelPlacement;
  activeTool: EditorTool;
  brushSettings: BrushSettings;
  textSettings: TextSettings;
  shapeSettings: ShapeSettings;
  marqueeSettings: MarqueeSettings;
  retouchSettings: RetouchSettings;
  penSettings: PenSettings;
  cloneSourcePoint: { x: number; y: number } | null;
  cloneArmed: boolean;
  onSetCloneSource: () => void;
  onClearCloneSource: () => void;
  onUpdateBrush: (settings: Partial<BrushSettings>) => void;
  onUpdateText: (settings: Partial<TextSettings>) => void;
  /** Text-on-path (Tier 2 Group A PR-E). When set, the user's
   *  current selection (active + multi-select) contains exactly
   *  one bound text layer — TextPanel surfaces an "Unbind" row.
   *  Computed by EditorPage off the selection union, so Multi mode
   *  tap-selecting a bound text + its vector works on mobile. */
  textPathBoundInfo?: { pathLayerId: string; pathLayerName: string } | null;
  /** When non-null, the user's current selection contains exactly
   *  one text + one vector layer (and no bound text) — TextPanel
   *  surfaces a "Bind to Path" row referring to the vector by name. */
  textPathBindCandidate?: { pathLayerId: string; pathLayerName: string } | null;
  /** True when the ACTIVE layer specifically is a bound text. Drives
   *  the Arch slider disabled state — when the bound text is the
   *  one the slider would operate on, arch is moot (path overrides).
   *  When a bound text is merely in multi-selection but not active,
   *  arch stays editable so the user can still edit the active
   *  text's arch. */
  isActiveTextBoundToPath?: boolean;
  onBindTextToPath?: () => void;
  onUnbindTextFromPath?: () => void;
  onUpdateShape: (settings: Partial<ShapeSettings>) => void;
  onUpdateMarquee: (settings: Partial<MarqueeSettings>) => void;
  onUpdateRetouch: (settings: RetouchSettingsPatch) => void;
  onUpdatePen: (settings: Partial<PenSettings>) => void;
  /** True when the active layer is a vector — surfaces the
   *  "Edit selected path…" button at the bottom of PenPanel. */
  penEditPathAvailable?: boolean;
  /** Open Path Edit mode on the currently-active vector layer. */
  onBeginPathEditActive?: () => void;
  /** Path Edit mode (Tier 2 Group A PR-B). When `pathEditActive`
   *  is true, the panel renders EditPathPanel in place of the
   *  active-tool panel. The remaining props drive its surface. */
  pathEditActive: boolean;
  pathEditSelectedAnchorIndex: number | null;
  pathEditSelectedAnchorType: AnchorType | null;
  onPathEditAnchorTypeChange: (type: AnchorType) => void;
  onApplyPathEdit: () => void;
  onCancelPathEdit: () => void;
  /** Vector boolean-ops chip row (Tier 2 Group A PR-C). The row
   *  renders when ≥2 vector layers are in the current selection
   *  (active + shift-extended) and no modal mode is active. The
   *  parent owns the actual fold logic; this panel only surfaces
   *  the four canonical ops. */
  eligibleVectorLayerCount: number;
  onVectorBooleanOp: (op: BooleanOp) => void;
  /** User picked a kind from the Adjustments picker. Parent opens
   *  the modal with the chosen kind's panel mounted. */
  onOpenAdjustment: (kind: AdjustmentKind) => void;
  /** User picked a kind from the Filter picker (Tier 2 Group C).
   *  Same parent flow as `onOpenAdjustment` — the modal is shared
   *  across adjustments and filters; the only UI difference is the
   *  title prefix. */
  onOpenFilter: (kind: AdjustmentKind) => void;
  /** Whether the active layer (or any of its descendants for a
   *  group) has rasterizable pixels to adjust. When false, the
   *  picker trigger renders disabled. */
  canAdjustActiveLayer: boolean;
  /** Selection-modify ops surfaced in the marquee tool panel and
   *  also via the StatusBar selection chip. (`hasMarqueeSelection`
   *  is declared lower for the AI tool — the same value is used.) */
  canGrowMarquee: boolean;
  onGrowMarqueeSelection: (deltaPx: number) => void;
  /** Invert the active marquee — flips selected/deselected within
   *  the image rect. Available for any selection shape. */
  onInvertMarqueeSelection: () => void;
  /** AI Select Subject — runs the active image through the
   *  remove-background pipeline server-side, installs the resulting
   *  binary mask as a wand-style marquee. Not always available
   *  (requires a loaded bg image and no other AI op in flight). */
  onSelectSubject?: () => void;
  selectSubjectBusy?: boolean;
  /** Tier 1 Group D — selection refinement entries surfaced in the
   *  MarqueePanel alongside the existing AI Select Subject /
   *  Invert / Grow-Shrink ops. Same handlers as the StatusBar chip
   *  popover so the same paths are reachable from both surfaces.
   *  Optional — callers that don't pass them get the prior MVP UI. */
  quickMaskActive?: boolean;
  onToggleQuickMask?: () => void;
  onOpenColorRange?: () => void;
  onOpenRefineEdge?: () => void;
  /** Switch the editor into Slice mode. Surfaced as the "Slices"
   *  button beneath Quick Mask in MarqueePanel — the Slice tool is
   *  hidden from the toolbar to save mobile space, so this is its
   *  replacement entry point. */
  onActivateSlices?: () => void;
  /** Quick Mask brush mode (Paint vs. Erase) + setter. Used by the
   *  dedicated QuickMaskPanel that renders when `activeTool ===
   *  'quickmask'`. Brush size/opacity come from `brushSettings`
   *  via the standard `onUpdateBrush` channel — no separate slider
   *  state. */
  quickMaskMode?: 'add' | 'subtract';
  onChangeQuickMaskMode?: (mode: 'add' | 'subtract') => void;
  /** Apply the QM painted mask via the chosen output target
   *  (Selection or Layer Mask). EditorPage owns the dispatch. */
  onApplyQuickMask?: (output: 'selection' | 'mask') => void;
  /** Cancel — discard the QM painted mask without applying. */
  onCancelQuickMask?: () => void;
  /** Whether the active layer can host a mask. False disables the
   *  "Layer Mask" output dropdown option. */
  canApplyQuickMaskAsLayerMask?: boolean;
  /** Free Transform (Tier 1 Group E Phase 1). When `transformActive`
   *  is true the TransformPanel replaces the active-tool panel. The
   *  panel reads live Fabric matrix via `getTransformMatrix` (rAF
   *  polled) and writes back via `onChangeTransformMatrix`. */
  transformActive?: boolean;
  transformMode?: 'free' | 'skew' | 'distort' | 'perspective' | 'warp';
  onChangeTransformMode?: (mode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp') => void;
  getTransformMatrix?: () => TransformMatrix | null;
  onChangeTransformMatrix?: (patch: Partial<TransformMatrix>) => void;
  onApplyTransform?: () => void;
  onCancelTransform?: () => void;
  /** Apply a Warp preset on the active Transform overlay. EditorPage
   *  wires this to `canvas.applyWarpPresetToOverlay`. The panel
   *  only renders the preset row when mode='warp', so this is no-op
   *  when undefined or wrong mode. */
  onApplyWarpPreset?: (preset: 'arc' | 'bulge' | 'twist' | 'wave') => void;
  /** Move-tool TransformPanel entry. EditorPage closes over the
   *  current activeLayerId; renders as a "Free Transform…" button
   *  below the rotate/flip row. Undefined when no active layer
   *  (button hides). */
  onBeginFreeTransform?: () => void;
  /** Move tool's Auto-Select preference and setter. Default `true`
   *  preserves the historical behavior (click-on-layer selects it).
   *  Toggling off switches the tool into Photoshop's drag-only-active
   *  mode — clicking anywhere on canvas drags whichever layer is
   *  currently active. Renders as a checkbox in the Move panel. */
  autoSelectLayers: boolean;
  onAutoSelectLayersChange: (value: boolean) => void;
  /** Whole-canvas transforms surfaced in the Move tool's properties. */
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onFlipHorizontal: () => void;
  onFlipVertical: () => void;
  /** AI tool — Smart Delete / Smart Create / etc. `hasMarqueeSelection`
   *  drives Smart Delete + Inpaint enable; `aiStatus` renders the inline
   *  banner. Smart Create is text-to-image only now (the previous "Blend"
   *  mode lives under the Inpaint picker entry). */
  hasMarqueeSelection: boolean;
  smartCreatePrompt: string;
  onSmartCreatePromptChange: (prompt: string) => void;
  smartCreateInsertModel: SmartCreateInsertModel;
  onSmartCreateInsertModelChange: (model: SmartCreateInsertModel) => void;
  aiStatus: AIStatus;
  onSmartDelete: () => void;
  onSmartCreate: () => void;
  /** Inpaint props — separate prompt field from Smart Create so the user
   *  doesn't lose either one when switching capabilities. `engine` picks
   *  the provider (Stability default at ~4 credits, Recraft / OpenAI at
   *  ~5). `negativePrompt` is forwarded to engines that support it
   *  (Stability). `growMaskPx` dilates the mask outward server-side.
   *  `featherRatio` drives the post-process edge blend (0 = hard edges,
   *  0.2 = very soft). */
  inpaintPrompt: string;
  onInpaintPromptChange: (prompt: string) => void;
  inpaintNegativePrompt: string;
  onInpaintNegativePromptChange: (prompt: string) => void;
  inpaintEngine: InpaintEngine;
  onInpaintEngineChange: (engine: InpaintEngine) => void;
  inpaintGrowMaskPx: number;
  onInpaintGrowMaskPxChange: (px: number) => void;
  inpaintFeatherRatio: number;
  onInpaintFeatherRatioChange: (ratio: number) => void;
  /** Engine-conditional style preset. Stability and Recraft both
   *  support a style param (different value sets); OpenAI does not.
   *  The dropdown is hidden when engine is OpenAI. Empty string means
   *  "engine default". The parent resets this to '' on engine change
   *  because the value sets are disjoint. */
  inpaintStylePreset: string;
  onInpaintStylePresetChange: (preset: string) => void;
  onInpaint: () => void;
  /** Smart Delete (paid) props — the paid Stability AI escalation
   *  inside the Smart Delete sub-panel. The "Smart Delete" button is
   *  the paid AI tier; the "Free Delete" button next to it runs the
   *  on-device MI-GAN model (`onSmartDelete`). `growMaskPx` dilates
   *  the mask outward server-side to catch edge halos when the marquee
   *  is tight (0–64 px). `featherRatio` is the same post-process blend
   *  used by Inpaint. */
  eraseGrowMaskPx: number;
  onEraseGrowMaskPxChange: (px: number) => void;
  eraseFeatherRatio: number;
  onEraseFeatherRatioChange: (ratio: number) => void;
  onErase: () => void;
  /** Search & Replace props — Stability auto-segments via
   *  `searchPrompt` (what to find) and replaces with `prompt` (what
   *  should appear). No marquee needed. `negativePrompt` and
   *  `growMaskPx` mirror the Inpaint controls. */
  searchReplaceSearchPrompt: string;
  onSearchReplaceSearchPromptChange: (next: string) => void;
  searchReplacePrompt: string;
  onSearchReplacePromptChange: (next: string) => void;
  searchReplaceNegativePrompt: string;
  onSearchReplaceNegativePromptChange: (next: string) => void;
  searchReplaceGrowMaskPx: number;
  onSearchReplaceGrowMaskPxChange: (px: number) => void;
  /** Optional Stability style preset (empty = default). Same 11
   *  named presets as Inpaint's Stability list. */
  searchReplaceStylePreset: string;
  onSearchReplaceStylePresetChange: (preset: string) => void;
  /** Optional seed for reproducibility. `null` = engine random. */
  searchReplaceSeed: number | null;
  onSearchReplaceSeedChange: (seed: number | null) => void;
  onSearchReplace: () => void;
  /** Search & Recolor props — Stability auto-segments via
   *  `selectPrompt` (what to recolor) and recolors with `prompt` (the
   *  new color/effect). Stability-only; no engine dropdown. */
  searchRecolorSelectPrompt: string;
  onSearchRecolorSelectPromptChange: (next: string) => void;
  searchRecolorPrompt: string;
  onSearchRecolorPromptChange: (next: string) => void;
  searchRecolorNegativePrompt: string;
  onSearchRecolorNegativePromptChange: (next: string) => void;
  searchRecolorGrowMaskPx: number;
  onSearchRecolorGrowMaskPxChange: (px: number) => void;
  searchRecolorStylePreset: string;
  onSearchRecolorStylePresetChange: (preset: string) => void;
  searchRecolorSeed: number | null;
  onSearchRecolorSeedChange: (seed: number | null) => void;
  onSearchRecolor: () => void;
  /** Outpaint props — extends the background image by N pixels on any
   *  combination of edges. Stability-only; no engine dropdown. */
  outpaintLeftPx: number;
  onOutpaintLeftPxChange: (px: number) => void;
  outpaintRightPx: number;
  onOutpaintRightPxChange: (px: number) => void;
  outpaintUpPx: number;
  onOutpaintUpPxChange: (px: number) => void;
  outpaintDownPx: number;
  onOutpaintDownPxChange: (px: number) => void;
  outpaintPrompt: string;
  onOutpaintPromptChange: (next: string) => void;
  outpaintCreativity: number;
  onOutpaintCreativityChange: (next: number) => void;
  outpaintNegativePrompt: string;
  onOutpaintNegativePromptChange: (next: string) => void;
  outpaintStylePreset: string;
  onOutpaintStylePresetChange: (preset: string) => void;
  outpaintSeed: number | null;
  onOutpaintSeedChange: (seed: number | null) => void;
  onOutpaint: () => void;
  /** Image to Image props — Stability strength-based reimagining via the
   *  `generate/*` endpoints in image-to-image mode. Stability-only;
   *  engine selects which Stability tier (Core / Ultra / SD3.5 Large). */
  i2iPrompt: string;
  onI2iPromptChange: (next: string) => void;
  i2iEngine: ImageToImageEngine;
  onI2iEngineChange: (engine: ImageToImageEngine) => void;
  /** Stability convention — 0 = identical to source, 1 = ignore source. */
  i2iStrength: number;
  onI2iStrengthChange: (next: number) => void;
  i2iNegativePrompt: string;
  onI2iNegativePromptChange: (next: string) => void;
  i2iStylePreset: string;
  onI2iStylePresetChange: (preset: string) => void;
  i2iSeed: number | null;
  onI2iSeedChange: (seed: number | null) => void;
  /** Replace the active layer's pixels in place vs add as a new
   *  editable layer aligned to the source. */
  i2iMode: EditorOutputMode;
  onI2iModeChange: (mode: EditorOutputMode) => void;
  onImageToImage: () => void;
  /** AI tool picker integration — `selectedAITool` drives which sub-panel
   *  renders inside the AI section; `onOpenAIPicker` re-opens the picker
   *  dialog from the panel header so the user can switch tools without
   *  leaving the AI section. `onRemoveBackground` is the existing one-shot
   *  handler that's now surfaced inside the Remove Background sub-panel. */
  selectedAITool: AICapability | null;
  onOpenAIPicker: () => void;
  onRemoveBackground: () => void;
  isRemovingBackground: boolean;
  canRemoveBackground: boolean;
  /** Remove-Background engine pulldown — `imgly` is the free client-side
   *  default; `stability` and `recraft` route through the paid backend. */
  removeBackgroundEngine: RemoveBackgroundEngine;
  onRemoveBackgroundEngineChange: (engine: RemoveBackgroundEngine) => void;
  /** Replace-Background controls — see `image-editor.types.ts` for the
   *  semantics of each field. Two engines (Recraft default, Stability
   *  upsell with relighting), prompt + optional reference images,
   *  light controls (Stability-only), advanced sliders, mode toggle. */
  replaceBgEngine: ReplaceBackgroundEngine;
  onReplaceBgEngineChange: (engine: ReplaceBackgroundEngine) => void;
  replaceBgPrompt: string;
  onReplaceBgPromptChange: (next: string) => void;
  replaceBgReferenceBlob: Blob | null;
  onReplaceBgReferenceBlobChange: (blob: Blob | null) => void;
  replaceBgLightReferenceBlob: Blob | null;
  onReplaceBgLightReferenceBlobChange: (blob: Blob | null) => void;
  replaceBgLightDirection: ReplaceBackgroundLightDirection;
  onReplaceBgLightDirectionChange: (dir: ReplaceBackgroundLightDirection) => void;
  replaceBgLightStrength: number;
  onReplaceBgLightStrengthChange: (n: number) => void;
  replaceBgPreserveSubject: number;
  onReplaceBgPreserveSubjectChange: (n: number) => void;
  replaceBgPreserveOriginalBg: number;
  onReplaceBgPreserveOriginalBgChange: (n: number) => void;
  replaceBgStyle: ReplaceBackgroundRecraftStyle | '';
  onReplaceBgStyleChange: (style: ReplaceBackgroundRecraftStyle | '') => void;
  replaceBgSeed: number | null;
  onReplaceBgSeedChange: (seed: number | null) => void;
  replaceBgMode: EditorOutputMode;
  onReplaceBgModeChange: (mode: EditorOutputMode) => void;
  onReplaceBackground: () => void;
  /** Upscale tool — three modes (fast/HQ/reimagine), contextual engine
   *  pulldown (Stability or Recraft, not always available per mode),
   *  optional creativity (Stability HQ + reimagine), optional factor
   *  (Recraft engines), seed (Stability HQ + reimagine), output mode. */
  upscaleMode: UpscaleMode;
  onUpscaleModeChange: (mode: UpscaleMode) => void;
  upscaleEngine: UpscaleEngine;
  onUpscaleEngineChange: (engine: UpscaleEngine) => void;
  upscalePrompt: string;
  onUpscalePromptChange: (next: string) => void;
  upscaleCreativity: number;
  onUpscaleCreativityChange: (n: number) => void;
  upscaleFactor: UpscaleFactor;
  onUpscaleFactorChange: (factor: UpscaleFactor) => void;
  upscaleSeed: number | null;
  onUpscaleSeedChange: (seed: number | null) => void;
  upscaleOutputMode: EditorOutputMode;
  onUpscaleOutputModeChange: (mode: EditorOutputMode) => void;
  /** Source dimensions in scene-space, used to render the
   *  "Source 1024×768 → ~4096×3072" hint near the action button. */
  upscaleSourceWidth: number;
  upscaleSourceHeight: number;
  onUpscale: () => void;
  /** Vectorize tool — single-engine (Recraft only), exports SVG via
   *  the OS save-as dialog. `vectorizeStatus` drives the panel's two
   *  states: 'idle' (button + description) vs 'has-result' (status
   *  note + Save again button so the user can recover from a
   *  cancelled save without re-running the API). */
  vectorizeStatus: 'idle' | 'saved' | 'cancelled';
  vectorizeSavedFileName: string | null;
  onVectorize: () => void;
  onSaveVectorizeAgain: () => void;
  /** Control / Guided — four Stability v2beta endpoints surfaced as
   *  modes in a single panel. Per-mode required fields drive the
   *  conditional UI inside the sub-panel. */
  controlMode: ControlMode;
  onControlModeChange: (mode: ControlMode) => void;
  controlPrompt: string;
  onControlPromptChange: (next: string) => void;
  controlNegativePrompt: string;
  onControlNegativePromptChange: (next: string) => void;
  controlStrength: number;
  onControlStrengthChange: (n: number) => void;
  controlFidelity: number;
  onControlFidelityChange: (n: number) => void;
  controlStyleReferenceBlob: Blob | null;
  onControlStyleReferenceBlobChange: (blob: Blob | null) => void;
  controlStyleStrength: number;
  onControlStyleStrengthChange: (n: number) => void;
  controlCompositionFidelity: number;
  onControlCompositionFidelityChange: (n: number) => void;
  controlChangeStrength: number;
  onControlChangeStrengthChange: (n: number) => void;
  controlSeed: number | null;
  onControlSeedChange: (seed: number | null) => void;
  controlOutputMode: EditorOutputMode;
  onControlOutputModeChange: (mode: EditorOutputMode) => void;
  onControl: () => void;
  onApplyCrop: () => void;
  onToggle: () => void;
  /** Open the help drawer at the active tool's docs. The header's
   *  `?` button calls this; EditorPage routes it to the right
   *  anchor based on `activeTool`. */
  onOpenHelp: () => void;
  /** Tier 2 Group F sub-piece #5 — Slice tool props. SlicePanel
   *  surfaces the slice list with rename + delete; the actual
   *  geometry editing happens on canvas via Fabric's native drag /
   *  resize controls. `canvasWidth` / `canvasHeight` are surfaced
   *  for the empty-state hint only. */
  slices?: readonly SliceRect[];
  onUpdateSlice?: (id: string, patch: Partial<Omit<SliceRect, 'id'>>) => void;
  onRemoveSlice?: (id: string) => void;
  onClearSlices?: () => void;
  canvasWidth?: number;
  canvasHeight?: number;
}

export function PropertiesPanel({
  isOpen,
  placement,
  activeTool,
  brushSettings,
  textSettings,
  shapeSettings,
  marqueeSettings,
  retouchSettings,
  penSettings,
  cloneSourcePoint,
  cloneArmed,
  onSetCloneSource,
  onClearCloneSource,
  onUpdateBrush,
  onUpdateText,
  textPathBoundInfo,
  textPathBindCandidate,
  isActiveTextBoundToPath,
  onBindTextToPath,
  onUnbindTextFromPath,
  onUpdateShape,
  onUpdateMarquee,
  onUpdateRetouch,
  onUpdatePen,
  penEditPathAvailable,
  onBeginPathEditActive,
  pathEditActive,
  pathEditSelectedAnchorIndex,
  pathEditSelectedAnchorType,
  onPathEditAnchorTypeChange,
  onApplyPathEdit,
  onCancelPathEdit,
  eligibleVectorLayerCount,
  onVectorBooleanOp,
  onOpenAdjustment,
  onOpenFilter,
  canAdjustActiveLayer,
  autoSelectLayers,
  onAutoSelectLayersChange,
  onRotateLeft,
  onRotateRight,
  onFlipHorizontal,
  onFlipVertical,
  hasMarqueeSelection,
  canGrowMarquee,
  onGrowMarqueeSelection,
  onInvertMarqueeSelection,
  onSelectSubject,
  selectSubjectBusy,
  quickMaskActive,
  onToggleQuickMask,
  onOpenColorRange,
  onOpenRefineEdge,
  onActivateSlices,
  quickMaskMode,
  onChangeQuickMaskMode,
  onApplyQuickMask,
  onCancelQuickMask,
  canApplyQuickMaskAsLayerMask,
  transformActive,
  transformMode,
  onChangeTransformMode,
  getTransformMatrix,
  onChangeTransformMatrix,
  onApplyTransform,
  onCancelTransform,
  onApplyWarpPreset,
  onBeginFreeTransform,
  smartCreatePrompt,
  onSmartCreatePromptChange,
  smartCreateInsertModel,
  onSmartCreateInsertModelChange,
  aiStatus,
  onSmartDelete,
  onSmartCreate,
  inpaintPrompt,
  onInpaintPromptChange,
  inpaintNegativePrompt,
  onInpaintNegativePromptChange,
  inpaintEngine,
  onInpaintEngineChange,
  inpaintGrowMaskPx,
  onInpaintGrowMaskPxChange,
  inpaintFeatherRatio,
  onInpaintFeatherRatioChange,
  inpaintStylePreset,
  onInpaintStylePresetChange,
  onInpaint,
  eraseGrowMaskPx,
  onEraseGrowMaskPxChange,
  eraseFeatherRatio,
  onEraseFeatherRatioChange,
  onErase,
  searchReplaceSearchPrompt,
  onSearchReplaceSearchPromptChange,
  searchReplacePrompt,
  onSearchReplacePromptChange,
  searchReplaceNegativePrompt,
  onSearchReplaceNegativePromptChange,
  searchReplaceGrowMaskPx,
  onSearchReplaceGrowMaskPxChange,
  searchReplaceStylePreset,
  onSearchReplaceStylePresetChange,
  searchReplaceSeed,
  onSearchReplaceSeedChange,
  onSearchReplace,
  searchRecolorSelectPrompt,
  onSearchRecolorSelectPromptChange,
  searchRecolorPrompt,
  onSearchRecolorPromptChange,
  searchRecolorNegativePrompt,
  onSearchRecolorNegativePromptChange,
  searchRecolorGrowMaskPx,
  onSearchRecolorGrowMaskPxChange,
  searchRecolorStylePreset,
  onSearchRecolorStylePresetChange,
  searchRecolorSeed,
  onSearchRecolorSeedChange,
  onSearchRecolor,
  outpaintLeftPx,
  onOutpaintLeftPxChange,
  outpaintRightPx,
  onOutpaintRightPxChange,
  outpaintUpPx,
  onOutpaintUpPxChange,
  outpaintDownPx,
  onOutpaintDownPxChange,
  outpaintPrompt,
  onOutpaintPromptChange,
  outpaintCreativity,
  onOutpaintCreativityChange,
  outpaintNegativePrompt,
  onOutpaintNegativePromptChange,
  outpaintStylePreset,
  onOutpaintStylePresetChange,
  outpaintSeed,
  onOutpaintSeedChange,
  onOutpaint,
  i2iPrompt,
  onI2iPromptChange,
  i2iEngine,
  onI2iEngineChange,
  i2iStrength,
  onI2iStrengthChange,
  i2iNegativePrompt,
  onI2iNegativePromptChange,
  i2iStylePreset,
  onI2iStylePresetChange,
  i2iSeed,
  onI2iSeedChange,
  i2iMode,
  onI2iModeChange,
  onImageToImage,
  selectedAITool,
  onOpenAIPicker,
  onRemoveBackground,
  isRemovingBackground,
  canRemoveBackground,
  removeBackgroundEngine,
  onRemoveBackgroundEngineChange,
  replaceBgEngine,
  onReplaceBgEngineChange,
  replaceBgPrompt,
  onReplaceBgPromptChange,
  replaceBgReferenceBlob,
  onReplaceBgReferenceBlobChange,
  replaceBgLightReferenceBlob,
  onReplaceBgLightReferenceBlobChange,
  replaceBgLightDirection,
  onReplaceBgLightDirectionChange,
  replaceBgLightStrength,
  onReplaceBgLightStrengthChange,
  replaceBgPreserveSubject,
  onReplaceBgPreserveSubjectChange,
  replaceBgPreserveOriginalBg,
  onReplaceBgPreserveOriginalBgChange,
  replaceBgStyle,
  onReplaceBgStyleChange,
  replaceBgSeed,
  onReplaceBgSeedChange,
  replaceBgMode,
  onReplaceBgModeChange,
  onReplaceBackground,
  upscaleMode,
  onUpscaleModeChange,
  upscaleEngine,
  onUpscaleEngineChange,
  upscalePrompt,
  onUpscalePromptChange,
  upscaleCreativity,
  onUpscaleCreativityChange,
  upscaleFactor,
  onUpscaleFactorChange,
  upscaleSeed,
  onUpscaleSeedChange,
  upscaleOutputMode,
  onUpscaleOutputModeChange,
  upscaleSourceWidth,
  upscaleSourceHeight,
  onUpscale,
  vectorizeStatus,
  vectorizeSavedFileName,
  onVectorize,
  onSaveVectorizeAgain,
  controlMode,
  onControlModeChange,
  controlPrompt,
  onControlPromptChange,
  controlNegativePrompt,
  onControlNegativePromptChange,
  controlStrength,
  onControlStrengthChange,
  controlFidelity,
  onControlFidelityChange,
  controlStyleReferenceBlob,
  onControlStyleReferenceBlobChange,
  controlStyleStrength,
  onControlStyleStrengthChange,
  controlCompositionFidelity,
  onControlCompositionFidelityChange,
  controlChangeStrength,
  onControlChangeStrengthChange,
  controlSeed,
  onControlSeedChange,
  controlOutputMode,
  onControlOutputModeChange,
  onControl,
  onApplyCrop,
  onToggle,
  onOpenHelp,
  slices,
  onUpdateSlice,
  onRemoveSlice,
  onClearSlices,
  canvasWidth,
  canvasHeight,
}: PropertiesPanelProps) {
  const isRight = placement === 'right';

  // Tier 3 Group D Slice 1a: recent colors moved into a persisted,
  // hook-backed singleton (`useRecentColors`). The Tier 0 panel-
  // scoped `useState` is gone; each `ColorSwatchPopover` reaches
  // into the hook itself, so we no longer need to prop-drill the
  // list or the push callback.

  if (!isOpen) {
    if (isRight) {
      return (
        <button
          type="button"
          className="w-full h-10 bg-muted border-l border-border flex items-center justify-between px-3 text-muted-foreground hover:text-foreground shrink-0"
          onClick={onToggle}
          title="Show properties"
        >
          <span className="text-xs font-medium">Properties</span>
          <ChevronDown className="h-4 w-4" />
        </button>
      );
    }
    return (
      <button
        type="button"
        className="w-full h-10 bg-muted border-t border-border flex items-center justify-between px-3 text-muted-foreground hover:text-foreground"
        onClick={onToggle}
        title="Show properties"
      >
        <span className="text-xs font-medium">Properties</span>
        <ChevronUp className="h-4 w-4" />
      </button>
    );
  }

  // Boolean-ops chip row visibility. Renders only in the "no
  // modal mode" branch — Path Edit, Free Transform, and Quick Mask
  // all take over the panel entirely, and combining vector shapes
  // doesn't apply mid-edit of a single path/raster. The row hugs
  // the top of the panel so it stays visible even when the active
  // tool's panel is long enough to require scroll.
  const showBooleanOpsRow =
    !pathEditActive && !transformActive && !quickMaskActive && eligibleVectorLayerCount >= 2;
  // Same top-of-panel slot as the boolean-ops row — surfacing here
  // (instead of only inside TextPanel) means the user discovers the
  // text-on-path UX without first switching to the Text tool. Same
  // modal-mode gates apply: path edit / free transform / quick mask
  // take over the panel and would visually compete with this row.
  const showTextPathBindRow =
    !pathEditActive && !transformActive && !quickMaskActive && !!textPathBindCandidate;
  const showTextPathBoundRow =
    !pathEditActive && !transformActive && !quickMaskActive && !!textPathBoundInfo;

  const body = (
    <div
      className={
        isRight
          ? 'overflow-y-auto flex-1 min-h-0 p-3 flex flex-col gap-y-4'
          : 'overflow-y-auto flex-1 min-h-0 p-3 flex flex-wrap gap-x-6 gap-y-4'
      }
    >
      {showBooleanOpsRow ? (
        <div className="w-full flex flex-col gap-1.5 pb-2 border-b border-border">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Combine {eligibleVectorLayerCount} shapes
          </span>
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(BOOLEAN_OP_LABELS) as BooleanOp[]).map((op) => (
              <button
                key={op}
                type="button"
                className="px-2.5 py-1 text-xs rounded-md border border-border bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                onClick={() => onVectorBooleanOp(op)}
                title={`${BOOLEAN_OP_LABELS[op]} — combine the selected vector shapes`}
              >
                {BOOLEAN_OP_LABELS[op]}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {showTextPathBoundRow && textPathBoundInfo ? (
        <div className="w-full flex flex-col gap-1.5 pb-2 border-b border-border">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Text on path
          </span>
          <div className="flex items-center gap-2 rounded border border-border bg-accent/30 px-2 py-1.5">
            <span className="text-xs flex-1 truncate" title={textPathBoundInfo.pathLayerName}>
              On path: {textPathBoundInfo.pathLayerName}
            </span>
            <button
              type="button"
              className="text-xs px-2 py-0.5 rounded bg-accent hover:bg-accent/80"
              onClick={onUnbindTextFromPath}
            >
              Unbind
            </button>
          </div>
        </div>
      ) : showTextPathBindRow && textPathBindCandidate ? (
        <div className="w-full flex flex-col gap-1.5 pb-2 border-b border-border">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Text on path
          </span>
          <div className="flex items-center gap-2 rounded border border-border bg-accent/30 px-2 py-1.5">
            <span className="text-xs flex-1 truncate" title={textPathBindCandidate.pathLayerName}>
              Selected path: {textPathBindCandidate.pathLayerName}
            </span>
            <button
              type="button"
              className="text-xs px-2 py-0.5 rounded bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={onBindTextToPath}
            >
              Bind to Path
            </button>
          </div>
        </div>
      ) : null}
      {pathEditActive ? (
        <EditPathPanel
          selectedAnchorIndex={pathEditSelectedAnchorIndex}
          selectedAnchorType={pathEditSelectedAnchorType}
          onAnchorTypeChange={onPathEditAnchorTypeChange}
          onApply={onApplyPathEdit}
          onCancel={onCancelPathEdit}
        />
      ) : transformActive ? (
        <FreeTransformPanel
          mode={transformMode ?? 'free'}
          onChangeMode={onChangeTransformMode ?? (() => {})}
          getMatrix={getTransformMatrix ?? (() => null)}
          onChangeMatrix={onChangeTransformMatrix ?? (() => {})}
          onApply={onApplyTransform ?? (() => {})}
          onCancel={onCancelTransform ?? (() => {})}
          onApplyWarpPreset={onApplyWarpPreset}
        />
      ) : activeTool === 'marquee' ? (
        <MarqueePanel
          settings={marqueeSettings}
          onChange={onUpdateMarquee}
          hasSelection={hasMarqueeSelection}
          canGrow={canGrowMarquee}
          onGrowSelection={onGrowMarqueeSelection}
          onInvertSelection={onInvertMarqueeSelection}
          onSelectSubject={onSelectSubject}
          selectSubjectBusy={selectSubjectBusy}
          quickMaskActive={quickMaskActive}
          onToggleQuickMask={onToggleQuickMask}
          onOpenColorRange={onOpenColorRange}
          onOpenRefineEdge={onOpenRefineEdge}
          onActivateSlices={onActivateSlices}
        />
      ) : activeTool === 'crop' ? (
        <CropPanel onApply={onApplyCrop} />
      ) : activeTool === 'quickmask' ? (
        <QuickMaskPanel
          brush={brushSettings}
          onChangeBrush={onUpdateBrush}
          mode={quickMaskMode ?? 'add'}
          onChangeMode={onChangeQuickMaskMode ?? (() => {})}
          onApply={onApplyQuickMask ?? (() => {})}
          onCancel={onCancelQuickMask ?? (() => {})}
          canApplyAsMask={canApplyQuickMaskAsLayerMask ?? false}
        />
      ) : (
        <>
          {activeTool === 'draw' && (
            <BrushPanel settings={brushSettings} onChange={onUpdateBrush} />
          )}
          {activeTool === 'eraser' && (
            <BrushPanel settings={brushSettings} onChange={onUpdateBrush} eraserMode />
          )}
          {activeTool === 'text' && (
            <TextPanel
              settings={textSettings}
              onChange={onUpdateText}
              isActiveTextBoundToPath={isActiveTextBoundToPath ?? false}
            />
          )}
          {activeTool === 'shape' && (
            <ShapePanel settings={shapeSettings} onChange={onUpdateShape} />
          )}
          {activeTool === 'fill' && <FillPanel settings={brushSettings} onChange={onUpdateBrush} />}
          {activeTool === 'retouch' && (
            <RetouchPanel
              settings={retouchSettings}
              onChange={onUpdateRetouch}
              cloneSourcePoint={cloneSourcePoint}
              cloneArmed={cloneArmed}
              onSetCloneSource={onSetCloneSource}
              onClearCloneSource={onClearCloneSource}
            />
          )}
          {activeTool === 'pen' && (
            <PenPanel
              settings={penSettings}
              onChange={onUpdatePen}
              editPathAvailable={penEditPathAvailable}
              onEditActivePath={onBeginPathEditActive}
            />
          )}
          {activeTool === 'slice' && onUpdateSlice && onRemoveSlice && onClearSlices && (
            <SlicePanel
              slices={slices ?? []}
              onUpdate={onUpdateSlice}
              onRemove={onRemoveSlice}
              onClearAll={onClearSlices}
              canvasWidth={canvasWidth ?? 0}
              canvasHeight={canvasHeight ?? 0}
            />
          )}
          {activeTool === 'ai' && (
            <AIPanel
              selectedAITool={selectedAITool}
              onOpenPicker={onOpenAIPicker}
              hasMarqueeSelection={hasMarqueeSelection}
              smartCreatePrompt={smartCreatePrompt}
              onPromptChange={onSmartCreatePromptChange}
              smartCreateInsertModel={smartCreateInsertModel}
              onSmartCreateInsertModelChange={onSmartCreateInsertModelChange}
              status={aiStatus}
              onSmartDelete={onSmartDelete}
              onSmartCreate={onSmartCreate}
              inpaintPrompt={inpaintPrompt}
              onInpaintPromptChange={onInpaintPromptChange}
              inpaintNegativePrompt={inpaintNegativePrompt}
              onInpaintNegativePromptChange={onInpaintNegativePromptChange}
              inpaintEngine={inpaintEngine}
              onInpaintEngineChange={onInpaintEngineChange}
              inpaintGrowMaskPx={inpaintGrowMaskPx}
              onInpaintGrowMaskPxChange={onInpaintGrowMaskPxChange}
              inpaintFeatherRatio={inpaintFeatherRatio}
              onInpaintFeatherRatioChange={onInpaintFeatherRatioChange}
              inpaintStylePreset={inpaintStylePreset}
              onInpaintStylePresetChange={onInpaintStylePresetChange}
              onInpaint={onInpaint}
              eraseGrowMaskPx={eraseGrowMaskPx}
              onEraseGrowMaskPxChange={onEraseGrowMaskPxChange}
              eraseFeatherRatio={eraseFeatherRatio}
              onEraseFeatherRatioChange={onEraseFeatherRatioChange}
              onErase={onErase}
              searchReplaceSearchPrompt={searchReplaceSearchPrompt}
              onSearchReplaceSearchPromptChange={onSearchReplaceSearchPromptChange}
              searchReplacePrompt={searchReplacePrompt}
              onSearchReplacePromptChange={onSearchReplacePromptChange}
              searchReplaceNegativePrompt={searchReplaceNegativePrompt}
              onSearchReplaceNegativePromptChange={onSearchReplaceNegativePromptChange}
              searchReplaceGrowMaskPx={searchReplaceGrowMaskPx}
              onSearchReplaceGrowMaskPxChange={onSearchReplaceGrowMaskPxChange}
              searchReplaceStylePreset={searchReplaceStylePreset}
              onSearchReplaceStylePresetChange={onSearchReplaceStylePresetChange}
              searchReplaceSeed={searchReplaceSeed}
              onSearchReplaceSeedChange={onSearchReplaceSeedChange}
              onSearchReplace={onSearchReplace}
              searchRecolorSelectPrompt={searchRecolorSelectPrompt}
              onSearchRecolorSelectPromptChange={onSearchRecolorSelectPromptChange}
              searchRecolorPrompt={searchRecolorPrompt}
              onSearchRecolorPromptChange={onSearchRecolorPromptChange}
              searchRecolorNegativePrompt={searchRecolorNegativePrompt}
              onSearchRecolorNegativePromptChange={onSearchRecolorNegativePromptChange}
              searchRecolorGrowMaskPx={searchRecolorGrowMaskPx}
              onSearchRecolorGrowMaskPxChange={onSearchRecolorGrowMaskPxChange}
              searchRecolorStylePreset={searchRecolorStylePreset}
              onSearchRecolorStylePresetChange={onSearchRecolorStylePresetChange}
              searchRecolorSeed={searchRecolorSeed}
              onSearchRecolorSeedChange={onSearchRecolorSeedChange}
              onSearchRecolor={onSearchRecolor}
              outpaintLeftPx={outpaintLeftPx}
              onOutpaintLeftPxChange={onOutpaintLeftPxChange}
              outpaintRightPx={outpaintRightPx}
              onOutpaintRightPxChange={onOutpaintRightPxChange}
              outpaintUpPx={outpaintUpPx}
              onOutpaintUpPxChange={onOutpaintUpPxChange}
              outpaintDownPx={outpaintDownPx}
              onOutpaintDownPxChange={onOutpaintDownPxChange}
              outpaintPrompt={outpaintPrompt}
              onOutpaintPromptChange={onOutpaintPromptChange}
              outpaintCreativity={outpaintCreativity}
              onOutpaintCreativityChange={onOutpaintCreativityChange}
              outpaintNegativePrompt={outpaintNegativePrompt}
              onOutpaintNegativePromptChange={onOutpaintNegativePromptChange}
              outpaintStylePreset={outpaintStylePreset}
              onOutpaintStylePresetChange={onOutpaintStylePresetChange}
              outpaintSeed={outpaintSeed}
              onOutpaintSeedChange={onOutpaintSeedChange}
              onOutpaint={onOutpaint}
              i2iPrompt={i2iPrompt}
              onI2iPromptChange={onI2iPromptChange}
              i2iEngine={i2iEngine}
              onI2iEngineChange={onI2iEngineChange}
              i2iStrength={i2iStrength}
              onI2iStrengthChange={onI2iStrengthChange}
              i2iNegativePrompt={i2iNegativePrompt}
              onI2iNegativePromptChange={onI2iNegativePromptChange}
              i2iStylePreset={i2iStylePreset}
              onI2iStylePresetChange={onI2iStylePresetChange}
              i2iSeed={i2iSeed}
              onI2iSeedChange={onI2iSeedChange}
              i2iMode={i2iMode}
              onI2iModeChange={onI2iModeChange}
              onImageToImage={onImageToImage}
              onRemoveBackground={onRemoveBackground}
              isRemovingBackground={isRemovingBackground}
              canRemoveBackground={canRemoveBackground}
              removeBackgroundEngine={removeBackgroundEngine}
              onRemoveBackgroundEngineChange={onRemoveBackgroundEngineChange}
              replaceBgEngine={replaceBgEngine}
              onReplaceBgEngineChange={onReplaceBgEngineChange}
              replaceBgPrompt={replaceBgPrompt}
              onReplaceBgPromptChange={onReplaceBgPromptChange}
              replaceBgReferenceBlob={replaceBgReferenceBlob}
              onReplaceBgReferenceBlobChange={onReplaceBgReferenceBlobChange}
              replaceBgLightReferenceBlob={replaceBgLightReferenceBlob}
              onReplaceBgLightReferenceBlobChange={onReplaceBgLightReferenceBlobChange}
              replaceBgLightDirection={replaceBgLightDirection}
              onReplaceBgLightDirectionChange={onReplaceBgLightDirectionChange}
              replaceBgLightStrength={replaceBgLightStrength}
              onReplaceBgLightStrengthChange={onReplaceBgLightStrengthChange}
              replaceBgPreserveSubject={replaceBgPreserveSubject}
              onReplaceBgPreserveSubjectChange={onReplaceBgPreserveSubjectChange}
              replaceBgPreserveOriginalBg={replaceBgPreserveOriginalBg}
              onReplaceBgPreserveOriginalBgChange={onReplaceBgPreserveOriginalBgChange}
              replaceBgStyle={replaceBgStyle}
              onReplaceBgStyleChange={onReplaceBgStyleChange}
              replaceBgSeed={replaceBgSeed}
              onReplaceBgSeedChange={onReplaceBgSeedChange}
              replaceBgMode={replaceBgMode}
              onReplaceBgModeChange={onReplaceBgModeChange}
              onReplaceBackground={onReplaceBackground}
              upscaleMode={upscaleMode}
              onUpscaleModeChange={onUpscaleModeChange}
              upscaleEngine={upscaleEngine}
              onUpscaleEngineChange={onUpscaleEngineChange}
              upscalePrompt={upscalePrompt}
              onUpscalePromptChange={onUpscalePromptChange}
              upscaleCreativity={upscaleCreativity}
              onUpscaleCreativityChange={onUpscaleCreativityChange}
              upscaleFactor={upscaleFactor}
              onUpscaleFactorChange={onUpscaleFactorChange}
              upscaleSeed={upscaleSeed}
              onUpscaleSeedChange={onUpscaleSeedChange}
              upscaleOutputMode={upscaleOutputMode}
              onUpscaleOutputModeChange={onUpscaleOutputModeChange}
              upscaleSourceWidth={upscaleSourceWidth}
              upscaleSourceHeight={upscaleSourceHeight}
              onUpscale={onUpscale}
              vectorizeStatus={vectorizeStatus}
              vectorizeSavedFileName={vectorizeSavedFileName}
              onVectorize={onVectorize}
              onSaveVectorizeAgain={onSaveVectorizeAgain}
              controlMode={controlMode}
              onControlModeChange={onControlModeChange}
              controlPrompt={controlPrompt}
              onControlPromptChange={onControlPromptChange}
              controlNegativePrompt={controlNegativePrompt}
              onControlNegativePromptChange={onControlNegativePromptChange}
              controlStrength={controlStrength}
              onControlStrengthChange={onControlStrengthChange}
              controlFidelity={controlFidelity}
              onControlFidelityChange={onControlFidelityChange}
              controlStyleReferenceBlob={controlStyleReferenceBlob}
              onControlStyleReferenceBlobChange={onControlStyleReferenceBlobChange}
              controlStyleStrength={controlStyleStrength}
              onControlStyleStrengthChange={onControlStyleStrengthChange}
              controlCompositionFidelity={controlCompositionFidelity}
              onControlCompositionFidelityChange={onControlCompositionFidelityChange}
              controlChangeStrength={controlChangeStrength}
              onControlChangeStrengthChange={onControlChangeStrengthChange}
              controlSeed={controlSeed}
              onControlSeedChange={onControlSeedChange}
              controlOutputMode={controlOutputMode}
              onControlOutputModeChange={onControlOutputModeChange}
              onControl={onControl}
            />
          )}
          {activeTool === 'move' && (
            // Tighter intra-section gap than the body's `gap-y-4` — Transform
            // and Filters are conceptually one cluster (whole-canvas
            // operations) so they should sit closer together.
            <div className="space-y-2">
              <MoveOptionsPanel
                autoSelectLayers={autoSelectLayers}
                onAutoSelectLayersChange={onAutoSelectLayersChange}
              />
              <TransformPanel
                onRotateLeft={onRotateLeft}
                onRotateRight={onRotateRight}
                onFlipHorizontal={onFlipHorizontal}
                onFlipVertical={onFlipVertical}
                onOpenFreeTransform={onBeginFreeTransform}
              />
              <FilterPanel
                onOpenAdjustment={onOpenAdjustment}
                onOpenFilter={onOpenFilter}
                canAdjustActiveLayer={canAdjustActiveLayer}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  const containerClass = isRight
    ? // flex-1 shares the rail height; min-h-32 (128px) is the practical
      // floor — header + a usable, internally-scrollable body. When the
      // rail can't fit every open panel's floor it scrolls (see EditorPage).
      'w-full bg-background border-l border-border flex flex-col flex-1 min-h-32'
    : 'w-full bg-background border-t border-border flex flex-col max-h-[50vh]';

  const HideIcon = isRight ? ChevronUp : ChevronDown;

  return (
    <div className={containerClass}>
      <div className="flex items-center justify-between px-3 h-10 bg-muted border-b border-border shrink-0">
        <h3 className="text-sm font-medium">Properties</h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
            onClick={onOpenHelp}
            title={`Help for ${activeTool} tool`}
            aria-label={`Open help for ${activeTool} tool`}
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            onClick={onToggle}
          >
            Hide
            <HideIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      {body}
    </div>
  );
}

const SECTION_CLASS = 'space-y-3 flex-1 min-w-[240px] max-w-sm';

function BrushPanel({
  settings,
  onChange,
  eraserMode,
}: {
  settings: BrushSettings;
  onChange: (s: Partial<BrushSettings>) => void;
  eraserMode?: boolean;
}) {
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">
        {eraserMode ? 'Eraser' : 'Brush'}
      </h4>
      {!eraserMode && (
        <label className="flex items-start gap-2">
          <span className="text-xs w-14 pt-2">Color</span>
          <ColorSwatchPopover
            value={{ type: 'solid', color: settings.color }}
            onChange={(v) => v.type === 'solid' && onChange({ color: v.color })}
            allowGradient={false}
            label="Brush color"
          />
        </label>
      )}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Size</span>
        <input
          type="range"
          min={1}
          max={100}
          value={settings.width}
          onChange={(e) => onChange({ width: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-8 text-right tabular-nums">{settings.width}</span>
      </label>
      {/* Opacity applies to both Draw (color blending strength) and Eraser
          (erase strength: 100% fully removes pixels, lower values reduce
          alpha proportionally). Shared with the Brush panel — there's one
          stroke-strength slider, contextual to the active stroke tool. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Opacity</span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(settings.opacity * 100)}
          onChange={(e) => onChange({ opacity: Number(e.target.value) / 100 })}
          className="flex-1"
        />
        <span className="text-xs w-8 text-right tabular-nums">
          {Math.round(settings.opacity * 100)}%
        </span>
      </label>
      {/* Feather softens the stroke edge at bake time. 0 = sharp
          (canvas2d's native anti-aliasing only); higher values
          progressively blur the baked stroke. The live preview stays
          crisp regardless — only the post-mouseup bake reflects the
          chosen feather. Shared between Draw + Eraser. Capped at
          50px to keep the cost bounded on large canvases (blur is
          GPU-accelerated but a 200px radius over a 4K canvas still
          drops frames). */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Feather</span>
        <input
          type="range"
          min={0}
          max={50}
          value={settings.feather}
          onChange={(e) => onChange({ feather: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-8 text-right tabular-nums">{settings.feather}px</span>
      </label>
    </div>
  );
}

// `AIStatus`, `SmartCreateMode`, `SmartCreateInsertModel` live in
// `../types/image-editor.types.ts` — they're shared between this
// component, the EditorPage handlers, and the API client. Re-exported
// here for back-compat with any external imports that referenced the
// previous declaration site.
export type { AIStatus, SmartCreateMode, SmartCreateInsertModel };

/** Smart Delete (paid) cost. Derived from the Stability erase entity's
 *  `perImageUsd × 1.20` (20% AI markup): $0.03 raw → 3.6 credits →
 *  rounded up to 4. Update if
 *  `packages/entities/src/providers/stability/erase/config.ts` pricing
 *  changes. */
const SMART_DELETE_COST_CREDITS = 4;

/** Per-engine credit cost surfaced in the Inpaint engine picker.
 *  Derived from each entity's `perImageUsd × 1.20` markup, rounded up:
 *    • Stability inpaint: $0.03 raw → 3.6 → 4
 *    • Recraft inpaint: $0.04 raw → 4.8 → 5
 *    • OpenAI gpt-image-1: $0.04 raw → 4.8 → 5 */
const INPAINT_ENGINE_COST_CREDITS: Record<InpaintEngine, number> = {
  stability: 4,
  recraft: 5,
  openai: 5,
};

/** Search & Replace cost. Stability's $0.04 raw × 1.20 markup → 4.8 →
 *  rounded up to 5. */
const SEARCH_REPLACE_COST_CREDITS = 5;

/** Search & Recolor cost. Stability's $0.05 raw × 1.20 markup → 6 →
 *  rounded up to 6. */
const SEARCH_RECOLOR_COST_CREDITS = 6;

/** Outpaint cost. Stability's $0.04 raw × 1.20 markup → 4.8 →
 *  rounded up to 5. */
const OUTPAINT_COST_CREDITS = 5;

const OUTPAINT_MAX_EDGE_PX = 2000;

/** Image to Image — per-engine cost. Stability raw × 1.20 markup, rounded up:
 *    Core:        $0.03 → 4
 *    Ultra:       $0.08 → 10
 *    SD3.5 Large: $0.065 → 8 */
const I2I_ENGINE_COST_CREDITS: Record<ImageToImageEngine, number> = {
  core: 4,
  ultra: 10,
  'sd3-large': 8,
};

const I2I_ENGINE_LABELS: Record<ImageToImageEngine, string> = {
  core: 'Stability Core',
  ultra: 'Stability Ultra',
  'sd3-large': 'Stability SD3.5 Large',
};

/** Remove-background — per-engine credit cost surfaced in the engine
 *  picker. `imgly` is free because it runs on the user's device.
 *  Stability and Recraft pricing follow the same `perImageUsd × 1.20`
 *  markup, rounded up:
 *    • imgly:     free
 *    • Stability: $0.02 raw → 2.4 → 3
 *    • Recraft:   $0.005 raw → 0.6 → 1 */
const REMOVE_BG_ENGINE_COST_CREDITS: Record<RemoveBackgroundEngine, number> = {
  imgly: 0,
  stability: 3,
  recraft: 1,
};

/** Replace-Background — per-engine credit cost. Recraft is the cheap
 *  default; Stability's relight model is the photoreal upsell.
 *    • Recraft:    $0.04 raw → 4.8 → 5
 *    • Stability:  $0.08 raw → 9.6 → 10 */
const REPLACE_BG_ENGINE_COST_CREDITS: Record<ReplaceBackgroundEngine, number> = {
  recraft: 5,
  stability: 10,
};

/** Control / Guided — per-mode credit cost. Pricing math is
 *  `perImageUsd × 1.20 markup`, rounded up:
 *    • from-sketch (control-sketch):           $0.03 → 4
 *    • match-structure (control-structure):    $0.03 → 4
 *    • match-style (control-style):            $0.04 → 5
 *    • style-transfer (control-style-transfer): $0.08 → 10 */
const CONTROL_COST_CREDITS: Record<ControlMode, number> = {
  'from-sketch': 4,
  'match-structure': 4,
  'match-style': 5,
  'style-transfer': 10,
};

const CONTROL_MODE_LABEL: Record<ControlMode, string> = {
  'from-sketch': 'From Sketch',
  'match-structure': 'Match Structure',
  'match-style': 'Match Style',
  'style-transfer': 'Style Transfer',
};

const CONTROL_MODE_DESCRIPTION: Record<ControlMode, string> = {
  'from-sketch':
    'Active layer is your sketch. Generate a polished image following its composition.',
  'match-structure':
    'Active layer’s shape is preserved. Generate new content with the same structure.',
  'match-style':
    'Active layer is the style reference. Generate a NEW image in this style, described by your prompt.',
  'style-transfer':
    'Keep your active layer’s content; apply the style of an uploaded reference image.',
};

/** Upscale — per-(mode, engine) credit cost. Pricing math is
 *  `perImageUsd × 1.20 markup`, rounded up. The wide spread between
 *  the fast tier (2 cr) and the premium tiers (24-30 cr) is a
 *  deliberate choice in the underlying Stability/Recraft pricing —
 *  we surface it prominently in the mode toggle so users don't
 *  click into a 30-credit operation by accident.
 *
 *  high-quality + recraft is `null` because Recraft has no
 *  preserve-detail premium tier. The dispatch falls through to a 400
 *  on the server and the panel hides the engine pulldown for HQ. */
const UPSCALE_COST_CREDITS: Record<UpscaleMode, Record<UpscaleEngine, number | null>> = {
  fast: { stability: 2, recraft: 2 },
  'high-quality': { stability: 30, recraft: null },
  reimagine: { stability: 30, recraft: 24 },
};

/** Per-mode label text. Used in the mode toggle and (truncated) on
 *  the action button. */
const UPSCALE_MODE_LABEL: Record<UpscaleMode, string> = {
  fast: 'Fast',
  'high-quality': 'High Quality',
  reimagine: 'Reimagine',
};

/** Engine-conditional style options for the Inpaint sub-panel. Stability
 *  exposes 11 named presets that bias the model toward a visual genre;
 *  Recraft has 4 modes that pick the underlying model family
 *  (realistic vs illustrated vs vector vs icon). OpenAI's gpt-image-1
 *  edits endpoint has no equivalent — we hide the dropdown when that
 *  engine is selected. */
const STABILITY_INPAINT_STYLE_OPTIONS: { value: string; label: string }[] = [
  { value: '3d-model', label: '3D model' },
  { value: 'analog-film', label: 'Analog film' },
  { value: 'anime', label: 'Anime' },
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'comic-book', label: 'Comic book' },
  { value: 'digital-art', label: 'Digital art' },
  { value: 'fantasy-art', label: 'Fantasy art' },
  { value: 'line-art', label: 'Line art' },
  { value: 'neon-punk', label: 'Neon punk' },
  { value: 'photographic', label: 'Photographic' },
  { value: 'pixel-art', label: 'Pixel art' },
];
const RECRAFT_INPAINT_STYLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'realistic_image', label: 'Realistic image' },
  { value: 'digital_illustration', label: 'Digital illustration' },
  { value: 'vector_illustration', label: 'Vector illustration' },
  { value: 'icon', label: 'Icon' },
];

/** Friendly labels for the capability picker breadcrumb at the top of the
 *  AI panel. Mirrors the labels in `AIToolPickerDialog` — not a barrel
 *  export because the dialog component owns the icon mapping too, and
 *  duplicating string literals is cheaper than coupling these two
 *  components through a shared metadata table. */
const AI_TOOL_LABELS: Record<AICapability, string> = {
  'smart-create': 'Smart Create',
  'smart-delete': 'Smart Delete',
  inpaint: 'Inpaint',
  'search-replace': 'Search & Replace',
  'search-recolor': 'Search & Recolor',
  outpaint: 'Outpaint',
  'image-to-image': 'Image to Image',
  'remove-background': 'Remove Background',
  'replace-background': 'Replace Background',
  upscale: 'Upscale',
  vectorize: 'Vectorize',
  control: 'Control / Guided',
};

function AIPanel({
  selectedAITool,
  onOpenPicker,
  hasMarqueeSelection,
  smartCreatePrompt,
  onPromptChange,
  smartCreateInsertModel,
  onSmartCreateInsertModelChange,
  status,
  onSmartDelete,
  onSmartCreate,
  inpaintPrompt,
  onInpaintPromptChange,
  inpaintNegativePrompt,
  onInpaintNegativePromptChange,
  inpaintEngine,
  onInpaintEngineChange,
  inpaintGrowMaskPx,
  onInpaintGrowMaskPxChange,
  inpaintFeatherRatio,
  onInpaintFeatherRatioChange,
  inpaintStylePreset,
  onInpaintStylePresetChange,
  onInpaint,
  eraseGrowMaskPx,
  onEraseGrowMaskPxChange,
  eraseFeatherRatio,
  onEraseFeatherRatioChange,
  onErase,
  searchReplaceSearchPrompt,
  onSearchReplaceSearchPromptChange,
  searchReplacePrompt,
  onSearchReplacePromptChange,
  searchReplaceNegativePrompt,
  onSearchReplaceNegativePromptChange,
  searchReplaceGrowMaskPx,
  onSearchReplaceGrowMaskPxChange,
  searchReplaceStylePreset,
  onSearchReplaceStylePresetChange,
  searchReplaceSeed,
  onSearchReplaceSeedChange,
  onSearchReplace,
  searchRecolorSelectPrompt,
  onSearchRecolorSelectPromptChange,
  searchRecolorPrompt,
  onSearchRecolorPromptChange,
  searchRecolorNegativePrompt,
  onSearchRecolorNegativePromptChange,
  searchRecolorGrowMaskPx,
  onSearchRecolorGrowMaskPxChange,
  searchRecolorStylePreset,
  onSearchRecolorStylePresetChange,
  searchRecolorSeed,
  onSearchRecolorSeedChange,
  onSearchRecolor,
  outpaintLeftPx,
  onOutpaintLeftPxChange,
  outpaintRightPx,
  onOutpaintRightPxChange,
  outpaintUpPx,
  onOutpaintUpPxChange,
  outpaintDownPx,
  onOutpaintDownPxChange,
  outpaintPrompt,
  onOutpaintPromptChange,
  outpaintCreativity,
  onOutpaintCreativityChange,
  outpaintNegativePrompt,
  onOutpaintNegativePromptChange,
  outpaintStylePreset,
  onOutpaintStylePresetChange,
  outpaintSeed,
  onOutpaintSeedChange,
  onOutpaint,
  i2iPrompt,
  onI2iPromptChange,
  i2iEngine,
  onI2iEngineChange,
  i2iStrength,
  onI2iStrengthChange,
  i2iNegativePrompt,
  onI2iNegativePromptChange,
  i2iStylePreset,
  onI2iStylePresetChange,
  i2iSeed,
  onI2iSeedChange,
  i2iMode,
  onI2iModeChange,
  onImageToImage,
  onRemoveBackground,
  isRemovingBackground,
  canRemoveBackground,
  removeBackgroundEngine,
  onRemoveBackgroundEngineChange,
  replaceBgEngine,
  onReplaceBgEngineChange,
  replaceBgPrompt,
  onReplaceBgPromptChange,
  replaceBgReferenceBlob,
  onReplaceBgReferenceBlobChange,
  replaceBgLightReferenceBlob,
  onReplaceBgLightReferenceBlobChange,
  replaceBgLightDirection,
  onReplaceBgLightDirectionChange,
  replaceBgLightStrength,
  onReplaceBgLightStrengthChange,
  replaceBgPreserveSubject,
  onReplaceBgPreserveSubjectChange,
  replaceBgPreserveOriginalBg,
  onReplaceBgPreserveOriginalBgChange,
  replaceBgStyle,
  onReplaceBgStyleChange,
  replaceBgSeed,
  onReplaceBgSeedChange,
  replaceBgMode,
  onReplaceBgModeChange,
  onReplaceBackground,
  upscaleMode,
  onUpscaleModeChange,
  upscaleEngine,
  onUpscaleEngineChange,
  upscalePrompt,
  onUpscalePromptChange,
  upscaleCreativity,
  onUpscaleCreativityChange,
  upscaleFactor,
  onUpscaleFactorChange,
  upscaleSeed,
  onUpscaleSeedChange,
  upscaleOutputMode,
  onUpscaleOutputModeChange,
  upscaleSourceWidth,
  upscaleSourceHeight,
  onUpscale,
  vectorizeStatus,
  vectorizeSavedFileName,
  onVectorize,
  onSaveVectorizeAgain,
  controlMode,
  onControlModeChange,
  controlPrompt,
  onControlPromptChange,
  controlNegativePrompt,
  onControlNegativePromptChange,
  controlStrength,
  onControlStrengthChange,
  controlFidelity,
  onControlFidelityChange,
  controlStyleReferenceBlob,
  onControlStyleReferenceBlobChange,
  controlStyleStrength,
  onControlStyleStrengthChange,
  controlCompositionFidelity,
  onControlCompositionFidelityChange,
  controlChangeStrength,
  onControlChangeStrengthChange,
  controlSeed,
  onControlSeedChange,
  controlOutputMode,
  onControlOutputModeChange,
  onControl,
}: {
  selectedAITool: AICapability | null;
  onOpenPicker: () => void;
  hasMarqueeSelection: boolean;
  smartCreatePrompt: string;
  onPromptChange: (next: string) => void;
  smartCreateInsertModel: SmartCreateInsertModel;
  onSmartCreateInsertModelChange: (model: SmartCreateInsertModel) => void;
  status: AIStatus;
  onSmartDelete: () => void;
  onSmartCreate: () => void;
  inpaintPrompt: string;
  onInpaintPromptChange: (next: string) => void;
  inpaintNegativePrompt: string;
  onInpaintNegativePromptChange: (next: string) => void;
  inpaintEngine: InpaintEngine;
  onInpaintEngineChange: (engine: InpaintEngine) => void;
  inpaintGrowMaskPx: number;
  onInpaintGrowMaskPxChange: (px: number) => void;
  inpaintFeatherRatio: number;
  onInpaintFeatherRatioChange: (next: number) => void;
  inpaintStylePreset: string;
  onInpaintStylePresetChange: (preset: string) => void;
  onInpaint: () => void;
  eraseGrowMaskPx: number;
  onEraseGrowMaskPxChange: (px: number) => void;
  eraseFeatherRatio: number;
  onEraseFeatherRatioChange: (ratio: number) => void;
  onErase: () => void;
  searchReplaceSearchPrompt: string;
  onSearchReplaceSearchPromptChange: (next: string) => void;
  searchReplacePrompt: string;
  onSearchReplacePromptChange: (next: string) => void;
  searchReplaceNegativePrompt: string;
  onSearchReplaceNegativePromptChange: (next: string) => void;
  searchReplaceGrowMaskPx: number;
  onSearchReplaceGrowMaskPxChange: (px: number) => void;
  searchReplaceStylePreset: string;
  onSearchReplaceStylePresetChange: (preset: string) => void;
  searchReplaceSeed: number | null;
  onSearchReplaceSeedChange: (seed: number | null) => void;
  onSearchReplace: () => void;
  searchRecolorSelectPrompt: string;
  onSearchRecolorSelectPromptChange: (next: string) => void;
  searchRecolorPrompt: string;
  onSearchRecolorPromptChange: (next: string) => void;
  searchRecolorNegativePrompt: string;
  onSearchRecolorNegativePromptChange: (next: string) => void;
  searchRecolorGrowMaskPx: number;
  onSearchRecolorGrowMaskPxChange: (px: number) => void;
  searchRecolorStylePreset: string;
  onSearchRecolorStylePresetChange: (preset: string) => void;
  searchRecolorSeed: number | null;
  onSearchRecolorSeedChange: (seed: number | null) => void;
  onSearchRecolor: () => void;
  outpaintLeftPx: number;
  onOutpaintLeftPxChange: (px: number) => void;
  outpaintRightPx: number;
  onOutpaintRightPxChange: (px: number) => void;
  outpaintUpPx: number;
  onOutpaintUpPxChange: (px: number) => void;
  outpaintDownPx: number;
  onOutpaintDownPxChange: (px: number) => void;
  outpaintPrompt: string;
  onOutpaintPromptChange: (next: string) => void;
  outpaintCreativity: number;
  onOutpaintCreativityChange: (next: number) => void;
  outpaintNegativePrompt: string;
  onOutpaintNegativePromptChange: (next: string) => void;
  outpaintStylePreset: string;
  onOutpaintStylePresetChange: (preset: string) => void;
  outpaintSeed: number | null;
  onOutpaintSeedChange: (seed: number | null) => void;
  onOutpaint: () => void;
  i2iPrompt: string;
  onI2iPromptChange: (next: string) => void;
  i2iEngine: ImageToImageEngine;
  onI2iEngineChange: (engine: ImageToImageEngine) => void;
  i2iStrength: number;
  onI2iStrengthChange: (next: number) => void;
  i2iNegativePrompt: string;
  onI2iNegativePromptChange: (next: string) => void;
  i2iStylePreset: string;
  onI2iStylePresetChange: (preset: string) => void;
  i2iSeed: number | null;
  onI2iSeedChange: (seed: number | null) => void;
  i2iMode: EditorOutputMode;
  onI2iModeChange: (mode: EditorOutputMode) => void;
  onImageToImage: () => void;
  onRemoveBackground: () => void;
  isRemovingBackground: boolean;
  canRemoveBackground: boolean;
  removeBackgroundEngine: RemoveBackgroundEngine;
  onRemoveBackgroundEngineChange: (engine: RemoveBackgroundEngine) => void;
  replaceBgEngine: ReplaceBackgroundEngine;
  onReplaceBgEngineChange: (engine: ReplaceBackgroundEngine) => void;
  replaceBgPrompt: string;
  onReplaceBgPromptChange: (next: string) => void;
  replaceBgReferenceBlob: Blob | null;
  onReplaceBgReferenceBlobChange: (blob: Blob | null) => void;
  replaceBgLightReferenceBlob: Blob | null;
  onReplaceBgLightReferenceBlobChange: (blob: Blob | null) => void;
  replaceBgLightDirection: ReplaceBackgroundLightDirection;
  onReplaceBgLightDirectionChange: (dir: ReplaceBackgroundLightDirection) => void;
  replaceBgLightStrength: number;
  onReplaceBgLightStrengthChange: (n: number) => void;
  replaceBgPreserveSubject: number;
  onReplaceBgPreserveSubjectChange: (n: number) => void;
  replaceBgPreserveOriginalBg: number;
  onReplaceBgPreserveOriginalBgChange: (n: number) => void;
  replaceBgStyle: ReplaceBackgroundRecraftStyle | '';
  onReplaceBgStyleChange: (style: ReplaceBackgroundRecraftStyle | '') => void;
  replaceBgSeed: number | null;
  onReplaceBgSeedChange: (seed: number | null) => void;
  replaceBgMode: EditorOutputMode;
  onReplaceBgModeChange: (mode: EditorOutputMode) => void;
  onReplaceBackground: () => void;
  upscaleMode: UpscaleMode;
  onUpscaleModeChange: (mode: UpscaleMode) => void;
  upscaleEngine: UpscaleEngine;
  onUpscaleEngineChange: (engine: UpscaleEngine) => void;
  upscalePrompt: string;
  onUpscalePromptChange: (next: string) => void;
  upscaleCreativity: number;
  onUpscaleCreativityChange: (n: number) => void;
  upscaleFactor: UpscaleFactor;
  onUpscaleFactorChange: (factor: UpscaleFactor) => void;
  upscaleSeed: number | null;
  onUpscaleSeedChange: (seed: number | null) => void;
  upscaleOutputMode: EditorOutputMode;
  onUpscaleOutputModeChange: (mode: EditorOutputMode) => void;
  upscaleSourceWidth: number;
  upscaleSourceHeight: number;
  onUpscale: () => void;
  vectorizeStatus: 'idle' | 'saved' | 'cancelled';
  vectorizeSavedFileName: string | null;
  onVectorize: () => void;
  onSaveVectorizeAgain: () => void;
  controlMode: ControlMode;
  onControlModeChange: (mode: ControlMode) => void;
  controlPrompt: string;
  onControlPromptChange: (next: string) => void;
  controlNegativePrompt: string;
  onControlNegativePromptChange: (next: string) => void;
  controlStrength: number;
  onControlStrengthChange: (n: number) => void;
  controlFidelity: number;
  onControlFidelityChange: (n: number) => void;
  controlStyleReferenceBlob: Blob | null;
  onControlStyleReferenceBlobChange: (blob: Blob | null) => void;
  controlStyleStrength: number;
  onControlStyleStrengthChange: (n: number) => void;
  controlCompositionFidelity: number;
  onControlCompositionFidelityChange: (n: number) => void;
  controlChangeStrength: number;
  onControlChangeStrengthChange: (n: number) => void;
  controlSeed: number | null;
  onControlSeedChange: (seed: number | null) => void;
  controlOutputMode: EditorOutputMode;
  onControlOutputModeChange: (mode: EditorOutputMode) => void;
  onControl: () => void;
}) {
  const busy = status.kind === 'loading-model' || status.kind === 'running';

  // No tool picked yet — surface a single CTA that opens the picker. The
  // picker auto-opens via EditorPage when the AI tool is first selected,
  // but if the user dismisses it, this gives them a way back in without
  // re-toggling the AI tool.
  if (selectedAITool === null) {
    return (
      <div className={SECTION_CLASS}>
        <h4 className="text-xs font-medium text-muted-foreground uppercase">AI</h4>
        <p className="text-xs text-muted-foreground">Pick an AI tool to see its options here.</p>
        <button
          type="button"
          onClick={onOpenPicker}
          className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent"
        >
          Choose AI tool…
        </button>
      </div>
    );
  }

  return (
    <div className={SECTION_CLASS}>
      {/* Header doubles as the "change tool" affordance — the user can
          jump back to the picker without leaving the AI tool. */}
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-medium text-muted-foreground uppercase">
          AI · {AI_TOOL_LABELS[selectedAITool]}
        </h4>
        <button
          type="button"
          onClick={onOpenPicker}
          className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          title="Pick a different AI tool"
        >
          Change
        </button>
      </div>

      {selectedAITool === 'smart-create' && (
        <SmartCreateSubPanel
          smartCreatePrompt={smartCreatePrompt}
          onPromptChange={onPromptChange}
          smartCreateInsertModel={smartCreateInsertModel}
          onSmartCreateInsertModelChange={onSmartCreateInsertModelChange}
          onSmartCreate={onSmartCreate}
          busy={busy}
        />
      )}
      {selectedAITool === 'smart-delete' && (
        <SmartDeleteSubPanel
          hasMarqueeSelection={hasMarqueeSelection}
          onSmartDelete={onSmartDelete}
          eraseGrowMaskPx={eraseGrowMaskPx}
          onEraseGrowMaskPxChange={onEraseGrowMaskPxChange}
          eraseFeatherRatio={eraseFeatherRatio}
          onEraseFeatherRatioChange={onEraseFeatherRatioChange}
          onErase={onErase}
          busy={busy}
        />
      )}
      {selectedAITool === 'remove-background' && (
        <RemoveBackgroundSubPanel
          onRemoveBackground={onRemoveBackground}
          isRemovingBackground={isRemovingBackground}
          canRemoveBackground={canRemoveBackground}
          engine={removeBackgroundEngine}
          onEngineChange={onRemoveBackgroundEngineChange}
          busy={busy}
        />
      )}
      {selectedAITool === 'inpaint' && (
        <InpaintSubPanel
          hasMarqueeSelection={hasMarqueeSelection}
          inpaintPrompt={inpaintPrompt}
          onPromptChange={onInpaintPromptChange}
          negativePrompt={inpaintNegativePrompt}
          onNegativePromptChange={onInpaintNegativePromptChange}
          engine={inpaintEngine}
          onEngineChange={onInpaintEngineChange}
          growMaskPx={inpaintGrowMaskPx}
          onGrowMaskPxChange={onInpaintGrowMaskPxChange}
          featherRatio={inpaintFeatherRatio}
          onFeatherRatioChange={onInpaintFeatherRatioChange}
          stylePreset={inpaintStylePreset}
          onStylePresetChange={onInpaintStylePresetChange}
          onInpaint={onInpaint}
          busy={busy}
        />
      )}
      {selectedAITool === 'outpaint' && (
        <OutpaintSubPanel
          leftPx={outpaintLeftPx}
          onLeftPxChange={onOutpaintLeftPxChange}
          rightPx={outpaintRightPx}
          onRightPxChange={onOutpaintRightPxChange}
          upPx={outpaintUpPx}
          onUpPxChange={onOutpaintUpPxChange}
          downPx={outpaintDownPx}
          onDownPxChange={onOutpaintDownPxChange}
          prompt={outpaintPrompt}
          onPromptChange={onOutpaintPromptChange}
          creativity={outpaintCreativity}
          onCreativityChange={onOutpaintCreativityChange}
          negativePrompt={outpaintNegativePrompt}
          onNegativePromptChange={onOutpaintNegativePromptChange}
          stylePreset={outpaintStylePreset}
          onStylePresetChange={onOutpaintStylePresetChange}
          seed={outpaintSeed}
          onSeedChange={onOutpaintSeedChange}
          onOutpaint={onOutpaint}
          busy={busy}
        />
      )}
      {selectedAITool === 'upscale' && (
        <UpscaleSubPanel
          mode={upscaleMode}
          onModeChange={onUpscaleModeChange}
          engine={upscaleEngine}
          onEngineChange={onUpscaleEngineChange}
          prompt={upscalePrompt}
          onPromptChange={onUpscalePromptChange}
          creativity={upscaleCreativity}
          onCreativityChange={onUpscaleCreativityChange}
          factor={upscaleFactor}
          onFactorChange={onUpscaleFactorChange}
          seed={upscaleSeed}
          onSeedChange={onUpscaleSeedChange}
          outputMode={upscaleOutputMode}
          onOutputModeChange={onUpscaleOutputModeChange}
          sourceWidth={upscaleSourceWidth}
          sourceHeight={upscaleSourceHeight}
          onUpscale={onUpscale}
          busy={busy}
        />
      )}
      {selectedAITool === 'search-recolor' && (
        <SearchRecolorSubPanel
          selectPrompt={searchRecolorSelectPrompt}
          onSelectPromptChange={onSearchRecolorSelectPromptChange}
          colorPrompt={searchRecolorPrompt}
          onColorPromptChange={onSearchRecolorPromptChange}
          negativePrompt={searchRecolorNegativePrompt}
          onNegativePromptChange={onSearchRecolorNegativePromptChange}
          growMaskPx={searchRecolorGrowMaskPx}
          onGrowMaskPxChange={onSearchRecolorGrowMaskPxChange}
          stylePreset={searchRecolorStylePreset}
          onStylePresetChange={onSearchRecolorStylePresetChange}
          seed={searchRecolorSeed}
          onSeedChange={onSearchRecolorSeedChange}
          onSearchRecolor={onSearchRecolor}
          busy={busy}
        />
      )}
      {selectedAITool === 'search-replace' && (
        <SearchReplaceSubPanel
          searchPrompt={searchReplaceSearchPrompt}
          onSearchPromptChange={onSearchReplaceSearchPromptChange}
          replacePrompt={searchReplacePrompt}
          onReplacePromptChange={onSearchReplacePromptChange}
          negativePrompt={searchReplaceNegativePrompt}
          onNegativePromptChange={onSearchReplaceNegativePromptChange}
          growMaskPx={searchReplaceGrowMaskPx}
          onGrowMaskPxChange={onSearchReplaceGrowMaskPxChange}
          stylePreset={searchReplaceStylePreset}
          onStylePresetChange={onSearchReplaceStylePresetChange}
          seed={searchReplaceSeed}
          onSeedChange={onSearchReplaceSeedChange}
          onSearchReplace={onSearchReplace}
          busy={busy}
        />
      )}
      {selectedAITool === 'image-to-image' && (
        <ImageToImageSubPanel
          prompt={i2iPrompt}
          onPromptChange={onI2iPromptChange}
          engine={i2iEngine}
          onEngineChange={onI2iEngineChange}
          strength={i2iStrength}
          onStrengthChange={onI2iStrengthChange}
          negativePrompt={i2iNegativePrompt}
          onNegativePromptChange={onI2iNegativePromptChange}
          stylePreset={i2iStylePreset}
          onStylePresetChange={onI2iStylePresetChange}
          seed={i2iSeed}
          onSeedChange={onI2iSeedChange}
          mode={i2iMode}
          onModeChange={onI2iModeChange}
          onImageToImage={onImageToImage}
          busy={busy}
        />
      )}
      {selectedAITool === 'replace-background' && (
        <ReplaceBackgroundSubPanel
          engine={replaceBgEngine}
          onEngineChange={onReplaceBgEngineChange}
          prompt={replaceBgPrompt}
          onPromptChange={onReplaceBgPromptChange}
          referenceBlob={replaceBgReferenceBlob}
          onReferenceBlobChange={onReplaceBgReferenceBlobChange}
          lightReferenceBlob={replaceBgLightReferenceBlob}
          onLightReferenceBlobChange={onReplaceBgLightReferenceBlobChange}
          lightDirection={replaceBgLightDirection}
          onLightDirectionChange={onReplaceBgLightDirectionChange}
          lightStrength={replaceBgLightStrength}
          onLightStrengthChange={onReplaceBgLightStrengthChange}
          preserveSubject={replaceBgPreserveSubject}
          onPreserveSubjectChange={onReplaceBgPreserveSubjectChange}
          preserveOriginalBg={replaceBgPreserveOriginalBg}
          onPreserveOriginalBgChange={onReplaceBgPreserveOriginalBgChange}
          style={replaceBgStyle}
          onStyleChange={onReplaceBgStyleChange}
          seed={replaceBgSeed}
          onSeedChange={onReplaceBgSeedChange}
          mode={replaceBgMode}
          onModeChange={onReplaceBgModeChange}
          onReplaceBackground={onReplaceBackground}
          busy={busy}
        />
      )}
      {selectedAITool === 'vectorize' && (
        <VectorizeSubPanel
          status={vectorizeStatus}
          savedFileName={vectorizeSavedFileName}
          onVectorize={onVectorize}
          onSaveAgain={onSaveVectorizeAgain}
          busy={busy}
        />
      )}
      {selectedAITool === 'control' && (
        <ControlSubPanel
          mode={controlMode}
          onModeChange={onControlModeChange}
          prompt={controlPrompt}
          onPromptChange={onControlPromptChange}
          negativePrompt={controlNegativePrompt}
          onNegativePromptChange={onControlNegativePromptChange}
          controlStrength={controlStrength}
          onControlStrengthChange={onControlStrengthChange}
          fidelity={controlFidelity}
          onFidelityChange={onControlFidelityChange}
          styleReferenceBlob={controlStyleReferenceBlob}
          onStyleReferenceBlobChange={onControlStyleReferenceBlobChange}
          styleStrength={controlStyleStrength}
          onStyleStrengthChange={onControlStyleStrengthChange}
          compositionFidelity={controlCompositionFidelity}
          onCompositionFidelityChange={onControlCompositionFidelityChange}
          changeStrength={controlChangeStrength}
          onChangeStrengthChange={onControlChangeStrengthChange}
          seed={controlSeed}
          onSeedChange={onControlSeedChange}
          outputMode={controlOutputMode}
          onOutputModeChange={onControlOutputModeChange}
          onControl={onControl}
          busy={busy}
        />
      )}

      {/* Inline status banner — shared across every sub-panel that runs
          an AI request, so it lives at the dispatcher level. */}
      {status.kind === 'loading-model' && (
        <div className="text-xs text-muted-foreground bg-accent/40 rounded-md px-3 py-2">
          Loading AI model…
          {typeof status.progressPct === 'number' && ` ${status.progressPct}%`}
        </div>
      )}
      {status.kind === 'running' && (
        <div className="text-xs text-muted-foreground bg-accent/40 rounded-md px-3 py-2">
          {status.label}
        </div>
      )}
      {status.kind === 'error' && (
        <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
          {status.message}
        </div>
      )}
    </div>
  );
}

function SmartCreateSubPanel({
  smartCreatePrompt,
  onPromptChange,
  smartCreateInsertModel,
  onSmartCreateInsertModelChange,
  onSmartCreate,
  busy,
}: {
  smartCreatePrompt: string;
  onPromptChange: (next: string) => void;
  smartCreateInsertModel: SmartCreateInsertModel;
  onSmartCreateInsertModelChange: (model: SmartCreateInsertModel) => void;
  onSmartCreate: () => void;
  busy: boolean;
}) {
  // Smart Create is text-to-image only — drops the result on a new layer.
  // The previous "Blend" mode (modify-the-marquee inpainting) has its own
  // picker entry under "Inpaint", with provider/engine choices in that
  // sub-panel.
  return (
    <div className="space-y-2">
      {/* Model picker — grouped by what each model is BEST AT, not by
          provider. The slug routes to a concrete (provider, model)
          tuple server-side. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Model</span>
        <select
          value={smartCreateInsertModel}
          onChange={(e) => onSmartCreateInsertModelChange(e.target.value as SmartCreateInsertModel)}
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
        >
          <optgroup label="Photorealistic">
            <option value="flux-2-pro">FLUX 2 Pro — fast, default</option>
            <option value="flux-ultra">FLUX 1.1 Ultra — 4 MP / hi-res</option>
            <option value="imagen-4">Imagen 4 — Google photo</option>
          </optgroup>
          <optgroup label="Specialty">
            <option value="gpt-image-1">GPT Image 1.5 — best text rendering</option>
            <option value="recraft-v3">Recraft V3 — illustration / icon</option>
            <option value="stable-ultra">Stable Ultra — 18 style presets</option>
          </optgroup>
          <optgroup label="Fast / cheap">
            <option value="nano-banana">Nano Banana — fastest, multi-image</option>
          </optgroup>
        </select>
      </label>

      <textarea
        value={smartCreatePrompt}
        onChange={(e) => onPromptChange(e.target.value)}
        placeholder="Describe what to create…"
        rows={3}
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5 resize-none"
        disabled={busy}
      />
      <p className="text-xs text-muted-foreground">
        Generates the subject on a transparent background and drops it on a new layer — sized to the
        marquee if one is active, otherwise centered.
      </p>
      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onSmartCreate}
        disabled={smartCreatePrompt.trim().length === 0 || busy}
      >
        Smart Create
      </button>
    </div>
  );
}

function InpaintSubPanel({
  hasMarqueeSelection,
  inpaintPrompt,
  onPromptChange,
  negativePrompt,
  onNegativePromptChange,
  engine,
  onEngineChange,
  growMaskPx,
  onGrowMaskPxChange,
  featherRatio,
  onFeatherRatioChange,
  stylePreset,
  onStylePresetChange,
  onInpaint,
  busy,
}: {
  hasMarqueeSelection: boolean;
  inpaintPrompt: string;
  onPromptChange: (next: string) => void;
  negativePrompt: string;
  onNegativePromptChange: (next: string) => void;
  engine: InpaintEngine;
  onEngineChange: (engine: InpaintEngine) => void;
  growMaskPx: number;
  onGrowMaskPxChange: (px: number) => void;
  featherRatio: number;
  onFeatherRatioChange: (next: number) => void;
  stylePreset: string;
  onStylePresetChange: (preset: string) => void;
  onInpaint: () => void;
  busy: boolean;
}) {
  const featherPct = Math.round(featherRatio * 100);
  // Pick the right option list for the current engine. OpenAI returns
  // an empty list which we use to suppress the entire <label>.
  const styleOptions =
    engine === 'stability'
      ? STABILITY_INPAINT_STYLE_OPTIONS
      : engine === 'recraft'
        ? RECRAFT_INPAINT_STYLE_OPTIONS
        : [];
  return (
    <div className="space-y-2">
      {/* Engine picker — Stability default (cheapest, supports negative
          prompt + grow mask), Recraft (replacement-oriented, prompt-only),
          OpenAI (best text rendering). Cost is shown in each option label
          so the choice is transparent. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Engine</span>
        <select
          value={engine}
          onChange={(e) => onEngineChange(e.target.value as InpaintEngine)}
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
        >
          <option value="stability">
            Stability Inpaint — {INPAINT_ENGINE_COST_CREDITS.stability} credits
          </option>
          <option value="recraft">
            Recraft Inpaint — {INPAINT_ENGINE_COST_CREDITS.recraft} credits
          </option>
          <option value="openai">
            OpenAI gpt-image-1 — {INPAINT_ENGINE_COST_CREDITS.openai} credits
          </option>
        </select>
      </label>

      {/* Engine-conditional style picker. Stability has 11 named
          presets, Recraft has 4 modes, OpenAI has none — so we hide
          the whole row when engine === 'openai'. The value sets are
          disjoint between Stability and Recraft, so the parent resets
          the picker to '' on engine change. */}
      {styleOptions.length > 0 && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-14">Style</span>
          <select
            value={stylePreset}
            onChange={(e) => onStylePresetChange(e.target.value)}
            className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
            disabled={busy}
            aria-label="Inpaint style preset"
          >
            <option value="">(default)</option>
            {styleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <textarea
        value={inpaintPrompt}
        onChange={(e) => onPromptChange(e.target.value)}
        placeholder="Describe what should appear in the masked area…"
        rows={3}
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5 resize-none"
        disabled={busy}
      />

      {/* Negative prompt — what to avoid. Forwarded to engines that
          support it server-side (Stability today; ignored otherwise).
          Single-line is plenty for typical "person, hands, distorted"
          style prompts. */}
      <input
        type="text"
        value={negativePrompt}
        onChange={(e) => onNegativePromptChange(e.target.value)}
        placeholder="Negative prompt — what to avoid (optional)"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
      />

      <p className="text-xs text-muted-foreground">
        Replaces the marquee selection with AI-generated content matching the prompt. Marquee
        required.
      </p>

      {/* Grow mask — push the mask outward server-side before the engine
          runs. Useful when the marquee just barely catches the subject
          and the result otherwise leaves an edge halo. Same control as
          Smart Delete; capped at 64 px. Stability honors this natively
          via `grow_mask`; the other engines ignore it. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Grow mask</span>
        <input
          type="range"
          min={0}
          max={64}
          step={1}
          value={growMaskPx}
          onChange={(e) => onGrowMaskPxChange(Number(e.target.value))}
          className="flex-1"
          disabled={busy}
          aria-label="Inpaint mask growth"
        />
        <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
          {growMaskPx} px
        </span>
      </label>

      {/* Feather slider — post-process edge blend. 0 = hard edges,
          higher = softer transition into surrounding pixels. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Feather</span>
        <input
          type="range"
          min={0}
          max={0.2}
          step={0.01}
          value={featherRatio}
          onChange={(e) => onFeatherRatioChange(Number(e.target.value))}
          className="flex-1"
          disabled={busy}
          aria-label="Inpaint mask feather"
        />
        <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
          {featherPct}%
        </span>
      </label>

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onInpaint}
        disabled={inpaintPrompt.trim().length === 0 || busy || !hasMarqueeSelection}
      >
        Inpaint
      </button>
    </div>
  );
}

function SmartDeleteSubPanel({
  hasMarqueeSelection,
  onSmartDelete,
  eraseGrowMaskPx,
  onEraseGrowMaskPxChange,
  eraseFeatherRatio,
  onEraseFeatherRatioChange,
  onErase,
  busy,
}: {
  hasMarqueeSelection: boolean;
  onSmartDelete: () => void;
  eraseGrowMaskPx: number;
  onEraseGrowMaskPxChange: (px: number) => void;
  eraseFeatherRatio: number;
  onEraseFeatherRatioChange: (ratio: number) => void;
  onErase: () => void;
  busy: boolean;
}) {
  // Two-tier layout under the "Smart Delete" tool name:
  //   - Free Delete: on-device MI-GAN. Free, fast, works offline.
  //   - Smart Delete (paid AI): Stability `/edit/erase`. ~4 credits/use.
  // Same marquee precondition for both. Recraft and OpenAI engines were
  // tried and dropped — Recraft's inpaint with a placeholder prompt only
  // lightened the masked region (it's replacement-oriented, not erase),
  // and OpenAI's gpt-image-1 invented pixelated content. Recraft will
  // come back as an option on the Inpaint tool.
  const eraseFeatherPct = Math.round(eraseFeatherRatio * 100);
  return (
    <div className="space-y-3">
      {/* Free Delete — on-device MI-GAN, runs subsecond on WebGPU. */}
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Removes the marquee selection using an on-device AI model. Free, fast, works offline.
        </p>
        <button
          type="button"
          className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
          onClick={onSmartDelete}
          disabled={!hasMarqueeSelection || busy}
        >
          Free Delete
        </button>
      </div>

      {/* Divider with the escalation hint — explicit so users know when
          to reach for the paid path instead of guessing. */}
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-wide">
        <span className="flex-1 h-px bg-border" />
        <span>or, if Free Delete leaves artifacts</span>
        <span className="flex-1 h-px bg-border" />
      </div>

      {/* Smart Delete (paid AI) — Stability `/edit/erase`. */}
      <div className="space-y-2">
        <h5 className="text-xs font-medium">Smart Delete (paid AI)</h5>
        <p className="text-xs text-muted-foreground">
          Runs on Stability AI&apos;s dedicated erase model. Better for complex backgrounds, large
          fills, and scenes the on-device model struggles with. ~{SMART_DELETE_COST_CREDITS} credits
          per use.
        </p>

        {/* Mask dilation — forwarded server-side as Stability's
            `grow_mask` form field. Useful when the marquee just barely
            catches the object and the result otherwise leaves a faint
            edge halo. 1 px steps; capped at 64 px (beyond that, you
            want Inpaint with a positive prompt). */}
        <label className="flex items-center gap-2">
          <span className="text-xs w-14">Grow mask</span>
          <input
            type="range"
            min={0}
            max={64}
            step={1}
            value={eraseGrowMaskPx}
            onChange={(e) => onEraseGrowMaskPxChange(Number(e.target.value))}
            className="flex-1"
            disabled={busy}
            aria-label="Smart Delete mask growth"
          />
          <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
            {eraseGrowMaskPx} px
          </span>
        </label>

        {/* Same feather-percent UI as Inpaint. */}
        <label className="flex items-center gap-2">
          <span className="text-xs w-14">Feather</span>
          <input
            type="range"
            min={0}
            max={0.2}
            step={0.01}
            value={eraseFeatherRatio}
            onChange={(e) => onEraseFeatherRatioChange(Number(e.target.value))}
            className="flex-1"
            disabled={busy}
            aria-label="Smart Delete feather"
          />
          <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
            {eraseFeatherPct}%
          </span>
        </label>

        <button
          type="button"
          className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
          onClick={onErase}
          disabled={!hasMarqueeSelection || busy}
        >
          Smart Delete
        </button>
      </div>
    </div>
  );
}

function RemoveBackgroundSubPanel({
  onRemoveBackground,
  isRemovingBackground,
  canRemoveBackground,
  engine,
  onEngineChange,
  busy,
}: {
  onRemoveBackground: () => void;
  isRemovingBackground: boolean;
  canRemoveBackground: boolean;
  engine: RemoveBackgroundEngine;
  onEngineChange: (engine: RemoveBackgroundEngine) => void;
  busy: boolean;
}) {
  // Engine picker semantics:
  //   • Free (imgly) — `@imgly/background-removal` ONNX in the browser.
  //     Fast, offline, $0. The default.
  //   • Stability — server-side `/edit/remove-background`. Stronger on
  //     hair / fur / fine detail; ~3 credits/use.
  //   • Recraft — server-side cutout. Cheapest paid option; ~1 credit/use.
  // The cost is shown in each <option> so the user understands the trade
  // before committing. We don't repeat it on the button.
  const buttonLabel = isRemovingBackground ? 'Removing background…' : 'Remove Background';
  const description =
    engine === 'imgly'
      ? 'Runs an on-device AI model. Free, fast, works offline.'
      : engine === 'stability'
        ? 'Stability AI server-side cutout — strong on hair, fur, and fine edges.'
        : 'Recraft server-side cutout — the cheapest paid option.';
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Engine</span>
        <select
          value={engine}
          onChange={(e) => onEngineChange(e.target.value as RemoveBackgroundEngine)}
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy || isRemovingBackground}
          aria-label="Remove background engine"
        >
          <option value="imgly">On-device (imgly) — free</option>
          <option value="stability">
            Stability — {REMOVE_BG_ENGINE_COST_CREDITS.stability} credits
          </option>
          <option value="recraft">Recraft — {REMOVE_BG_ENGINE_COST_CREDITS.recraft} credits</option>
        </select>
      </label>

      <p className="text-xs text-muted-foreground">
        Strip the background from the active image, leaving only the subject on a transparent layer.{' '}
        {description}
      </p>

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onRemoveBackground}
        disabled={!canRemoveBackground || isRemovingBackground || busy}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

function ReplaceBackgroundSubPanel({
  engine,
  onEngineChange,
  prompt,
  onPromptChange,
  referenceBlob,
  onReferenceBlobChange,
  lightReferenceBlob,
  onLightReferenceBlobChange,
  lightDirection,
  onLightDirectionChange,
  lightStrength,
  onLightStrengthChange,
  preserveSubject,
  onPreserveSubjectChange,
  preserveOriginalBg,
  onPreserveOriginalBgChange,
  style,
  onStyleChange,
  seed,
  onSeedChange,
  mode,
  onModeChange,
  onReplaceBackground,
  busy,
}: {
  engine: ReplaceBackgroundEngine;
  onEngineChange: (engine: ReplaceBackgroundEngine) => void;
  prompt: string;
  onPromptChange: (next: string) => void;
  referenceBlob: Blob | null;
  onReferenceBlobChange: (blob: Blob | null) => void;
  lightReferenceBlob: Blob | null;
  onLightReferenceBlobChange: (blob: Blob | null) => void;
  lightDirection: ReplaceBackgroundLightDirection;
  onLightDirectionChange: (dir: ReplaceBackgroundLightDirection) => void;
  lightStrength: number;
  onLightStrengthChange: (n: number) => void;
  preserveSubject: number;
  onPreserveSubjectChange: (n: number) => void;
  preserveOriginalBg: number;
  onPreserveOriginalBgChange: (n: number) => void;
  style: ReplaceBackgroundRecraftStyle | '';
  onStyleChange: (style: ReplaceBackgroundRecraftStyle | '') => void;
  seed: number | null;
  onSeedChange: (seed: number | null) => void;
  mode: EditorOutputMode;
  onModeChange: (mode: EditorOutputMode) => void;
  onReplaceBackground: () => void;
  busy: boolean;
}) {
  // Two engines, very different feature surfaces:
  //   • Recraft (default) — prompt-only swap, optional style preset.
  //     Subject pixels are preserved as-is (no relighting).
  //   • Stability — photoreal swap with relighting + reference image
  //     support. Heavier endpoint, ~2× the cost.
  // Both segment the subject internally — caller passes the FULL
  // source image, not a pre-cut transparent PNG.
  const cost = REPLACE_BG_ENGINE_COST_CREDITS[engine];
  // Stability accepts EITHER a prompt OR a reference image; Recraft
  // requires a prompt. The action button uses the right gate per engine.
  const ready =
    engine === 'stability'
      ? prompt.trim().length > 0 || referenceBlob !== null
      : prompt.trim().length > 0;
  const showStabilityControls = engine === 'stability';
  const showRecraftStyle = engine === 'recraft';
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Swap the image&apos;s background with a prompt-described scene
        {showStabilityControls ? ' and re-light the subject to match' : ''}. ~{cost} credits per
        use.
      </p>

      {/* Tier 1 — engine pulldown. Cost in label so the trade is visible
          before the user commits. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Engine</span>
        <select
          value={engine}
          onChange={(e) => onEngineChange(e.target.value as ReplaceBackgroundEngine)}
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Replace background engine"
        >
          <option value="recraft">
            Recraft — {REPLACE_BG_ENGINE_COST_CREDITS.recraft} credits
          </option>
          <option value="stability">
            Stability + Relight — {REPLACE_BG_ENGINE_COST_CREDITS.stability} credits
          </option>
        </select>
      </label>
      {/* Engine-choice hint — Stability ALWAYS relights the subject
          (the endpoint is named `replace-background-and-relight` and
          there's no opt-out). Spelling that out at the engine level
          steers users to Recraft when they want the original lighting
          preserved, instead of paying for a relight they don't want
          and being surprised. */}
      <p className="text-[11px] text-muted-foreground leading-snug">
        {showStabilityControls
          ? 'Stability re-lights the subject to match the new background. Pull “Preserve subject” up to dampen.'
          : 'Recraft swaps the background only — the subject’s lighting is kept as-is.'}
      </p>

      {/* Tier 1 — prompt textarea. Visible label so it's hard to confuse
          with any single-line input below (same pattern as I2I). */}
      <label className="text-xs block">
        <span className="text-xs text-muted-foreground">
          Background prompt
          {showStabilityControls && referenceBlob !== null ? ' (optional with reference)' : ''}
        </span>
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder="Describe the new background…"
          rows={3}
          className="mt-1 w-full text-xs bg-background border border-border rounded-md px-2 py-1.5 resize-none"
          disabled={busy}
          aria-label="Replace background prompt"
        />
      </label>

      {/* Tier 1 (Stability only) — background reference image. Either
          this OR the prompt is required for Stability; both can be set
          for a blended outcome. */}
      {showStabilityControls && (
        <ReferenceImageDrop
          value={referenceBlob}
          onChange={onReferenceBlobChange}
          label="Background reference (optional)"
          hint="Stability matches the new background to this image's look."
          disabled={busy}
        />
      )}

      {/* Tier 1 — output mode. Same segmented control + default as I2I. */}
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">Output</span>
        <div
          className="grid grid-cols-2 gap-1 rounded-md border border-border p-0.5 bg-background"
          role="radiogroup"
          aria-label="Replace background output mode"
        >
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'replace'}
            disabled={busy}
            className={`px-2 py-1 text-xs rounded ${
              mode === 'replace'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            } disabled:opacity-50 disabled:pointer-events-none`}
            onClick={() => onModeChange('replace')}
          >
            Replace layer
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'new-layer'}
            disabled={busy}
            className={`px-2 py-1 text-xs rounded ${
              mode === 'new-layer'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            } disabled:opacity-50 disabled:pointer-events-none`}
            onClick={() => onModeChange('new-layer')}
          >
            New layer
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          {mode === 'replace'
            ? 'Bakes the result into the active layer — destructive.'
            : 'Drops the result on top as an editable layer — easy to compare or discard.'}
        </p>
      </div>

      {/* Tier 2 (Stability only) — light controls. Direction is a
          segmented selector with `(none)` as the default so we don't
          surprise users with an unrequested relight. Strength only
          applies when a direction is picked. */}
      {showStabilityControls && (
        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Light direction</span>
          <div
            className="grid grid-cols-5 gap-1 rounded-md border border-border p-0.5 bg-background"
            role="radiogroup"
            aria-label="Light source direction"
          >
            {(['none', 'above', 'below', 'left', 'right'] as ReplaceBackgroundLightDirection[]).map(
              (dir) => (
                <button
                  key={dir}
                  type="button"
                  role="radio"
                  aria-checked={lightDirection === dir}
                  disabled={busy}
                  className={`px-1.5 py-1 text-[11px] rounded ${
                    lightDirection === dir
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent'
                  } disabled:opacity-50 disabled:pointer-events-none capitalize`}
                  onClick={() => onLightDirectionChange(dir)}
                >
                  {dir === 'none' ? '(none)' : dir}
                </button>
              ),
            )}
          </div>
          <label className="flex items-center gap-2">
            <span className="text-xs w-16">Strength</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={lightStrength}
              onChange={(e) => onLightStrengthChange(Number(e.target.value))}
              className="flex-1"
              disabled={busy || lightDirection === 'none'}
              aria-label="Light source strength"
            />
            <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
              {lightStrength.toFixed(2)}
            </span>
          </label>
        </div>
      )}

      {/* Tier 2 (Stability only) — light reference image. Niche but
          high-leverage when the user wants to match the lighting of an
          existing photo rather than describe it. */}
      {showStabilityControls && (
        <ReferenceImageDrop
          value={lightReferenceBlob}
          onChange={onLightReferenceBlobChange}
          label="Light reference (optional)"
          hint="Stability mimics this image's lighting in the result."
          disabled={busy}
        />
      )}

      {/* Tier 2 (Recraft only) — style preset. Same enum as Recraft
          inpaint; reusable list. */}
      {showRecraftStyle && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-16">Style</span>
          <select
            value={style}
            onChange={(e) => onStyleChange(e.target.value as ReplaceBackgroundRecraftStyle | '')}
            className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
            disabled={busy}
            aria-label="Recraft style preset"
          >
            <option value="">(default)</option>
            {RECRAFT_INPAINT_STYLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {/* Tier 3 — preserve sliders (Stability only). 0.5 / 0 are sane
          defaults — `preserveSubject` is the #1 user-facing knob for
          relight tools (controls how much the subject's appearance is
          locked). */}
      {showStabilityControls && (
        <>
          <label className="flex items-center gap-2">
            <span className="text-xs w-24">Preserve subject</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={preserveSubject}
              onChange={(e) => onPreserveSubjectChange(Number(e.target.value))}
              className="flex-1"
              disabled={busy}
              aria-label="Preserve original subject"
            />
            <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
              {preserveSubject.toFixed(2)}
            </span>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-xs w-24">Preserve old BG</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={preserveOriginalBg}
              onChange={(e) => onPreserveOriginalBgChange(Number(e.target.value))}
              className="flex-1"
              disabled={busy}
              aria-label="Preserve original background"
            />
            <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
              {preserveOriginalBg.toFixed(2)}
            </span>
          </label>
        </>
      )}

      {/* Tier 3 — seed. Same UX as I2I / S&R. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Seed</span>
        <input
          type="number"
          min={0}
          max={4_294_967_295}
          step={1}
          value={seed ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onSeedChange(null);
              return;
            }
            const parsed = Number(raw);
            if (Number.isFinite(parsed) && parsed >= 0) {
              onSeedChange(Math.floor(parsed));
            }
          }}
          placeholder="random"
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Replace background seed"
        />
      </label>

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onReplaceBackground}
        disabled={!ready || busy}
      >
        Replace Background
      </button>
    </div>
  );
}

function UpscaleSubPanel({
  mode,
  onModeChange,
  engine,
  onEngineChange,
  prompt,
  onPromptChange,
  creativity,
  onCreativityChange,
  factor,
  onFactorChange,
  seed,
  onSeedChange,
  outputMode,
  onOutputModeChange,
  sourceWidth,
  sourceHeight,
  onUpscale,
  busy,
}: {
  mode: UpscaleMode;
  onModeChange: (mode: UpscaleMode) => void;
  engine: UpscaleEngine;
  onEngineChange: (engine: UpscaleEngine) => void;
  prompt: string;
  onPromptChange: (next: string) => void;
  creativity: number;
  onCreativityChange: (n: number) => void;
  factor: UpscaleFactor;
  onFactorChange: (factor: UpscaleFactor) => void;
  seed: number | null;
  onSeedChange: (seed: number | null) => void;
  outputMode: EditorOutputMode;
  onOutputModeChange: (mode: EditorOutputMode) => void;
  sourceWidth: number;
  sourceHeight: number;
  onUpscale: () => void;
  busy: boolean;
}) {
  // Mode-first design — three buttons surface the cost-vs-quality
  // tradeoff up front. Engine pulldown is contextual: hidden on
  // high-quality (Stability is the only option), shown on fast and
  // reimagine where there's a real choice. The (mode, engine) pair
  // dispatches server-side to one of five entities.
  //
  // High-quality + Stability is the only path that requires a prompt;
  // reimagine + Stability also accepts one. Recraft engines never
  // accept a prompt server-side, so we hide the textarea entirely on
  // those branches.
  const stabilityCost = UPSCALE_COST_CREDITS[mode].stability;
  const recraftCost = UPSCALE_COST_CREDITS[mode].recraft;
  const hqOnlyStability = mode === 'high-quality';
  const showEngine = !hqOnlyStability;
  // Effective engine — when the user is on HQ, the dispatch is forced
  // to Stability regardless of the local pulldown state. Mirroring
  // that here means the controls (creativity slider on, factor picker
  // off) match what the server will actually do.
  const effectiveEngine = hqOnlyStability ? 'stability' : engine;
  const isStability = effectiveEngine === 'stability';
  const isRecraft = effectiveEngine === 'recraft';
  const acceptsPrompt = isStability && (mode === 'high-quality' || mode === 'reimagine');
  const showCreativity = acceptsPrompt;
  const showFactor = isRecraft && (mode === 'fast' || mode === 'reimagine');
  const showSeed = acceptsPrompt;
  const ready = !acceptsPrompt || prompt.trim().length > 0;
  const cost = isStability ? stabilityCost : recraftCost;

  // Projected output dimensions. Recraft engines explicitly take a
  // factor. Stability fast is 4×; Stability HQ + reimagine are
  // approximately 4× per the API docs (target ≈ 4 megapixels). We
  // surface the rough projection so the user isn't surprised by the
  // result size.
  const upscaleFactor = isRecraft ? factor : 4;
  const projW = sourceWidth > 0 ? sourceWidth * upscaleFactor : 0;
  const projH = sourceHeight > 0 ? sourceHeight * upscaleFactor : 0;
  const dims =
    sourceWidth > 0 && sourceHeight > 0
      ? `${sourceWidth}×${sourceHeight} → ~${projW}×${projH}`
      : null;

  return (
    <div className="space-y-2">
      {/* Tier 1 — mode toggle. The cost spread between fast (2 cr) and
          HQ/reimagine (24-30 cr) is wide enough that we surface it on
          the mode buttons themselves so the user can't accidentally
          click into a 30-credit op. */}
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">Mode</span>
        <div
          className="grid grid-cols-3 gap-1 rounded-md border border-border p-0.5 bg-background"
          role="radiogroup"
          aria-label="Upscale mode"
        >
          {(['fast', 'high-quality', 'reimagine'] as UpscaleMode[]).map((m) => {
            // For each mode, surface the cheapest engine's cost as the
            // headline number. HQ has only Stability (30); fast has
            // both at 2; reimagine ranges 24-30 with Recraft cheaper.
            const stability = UPSCALE_COST_CREDITS[m].stability ?? 0;
            const recraft = UPSCALE_COST_CREDITS[m].recraft;
            const minCost = recraft !== null ? Math.min(stability, recraft) : stability;
            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                disabled={busy}
                className={`px-1.5 py-1 text-[11px] rounded ${
                  mode === m
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent'
                } disabled:opacity-50 disabled:pointer-events-none flex flex-col items-center leading-tight`}
                onClick={() => onModeChange(m)}
              >
                <span className="text-xs">{UPSCALE_MODE_LABEL[m]}</span>
                <span className="text-[10px] opacity-80">{minCost} cr</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          {mode === 'fast'
            ? 'Clean 4× upscale, preserves the original. Cheapest tier.'
            : mode === 'high-quality'
              ? 'Premium hi-res upscaler that sharpens detail. Stability only — needs a prompt.'
              : 'Heavy upscale that reimagines detail at higher resolution.'}
        </p>
      </div>

      {/* Tier 1 — engine. Hidden when the mode forces a specific engine
          (high-quality is Stability-only). */}
      {showEngine && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-16">Engine</span>
          <select
            value={engine}
            onChange={(e) => onEngineChange(e.target.value as UpscaleEngine)}
            className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
            disabled={busy}
            aria-label="Upscale engine"
          >
            <option value="stability">Stability — {stabilityCost} cr</option>
            {recraftCost !== null && <option value="recraft">Recraft — {recraftCost} cr</option>}
          </select>
        </label>
      )}

      {/* Tier 1 — prompt. Stability HQ + reimagine require it; Recraft
          and Stability Fast don't accept it. We hide the field entirely
          when it's not in play instead of disabling — keeps the panel
          tight on the cheap path. */}
      {acceptsPrompt && (
        <label className="text-xs block">
          <span className="text-xs text-muted-foreground">Prompt</span>
          <textarea
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder="Describe the upscaled image…"
            rows={3}
            className="mt-1 w-full text-xs bg-background border border-border rounded-md px-2 py-1.5 resize-none"
            disabled={busy}
            aria-label="Upscale prompt"
          />
        </label>
      )}

      {/* Tier 2 — creativity slider (Stability HQ + reimagine only).
          Range differs per endpoint:
            • HQ (conservative):  0.2..0.35 — Stability rejects < 0.2
            • Reimagine (creative): 0..0.35
          The slider's `min` shifts so the user can never drag below
          the HQ floor and burn a credit reservation on a doomed call. */}
      {showCreativity && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-20">Creativity</span>
          <input
            type="range"
            min={mode === 'high-quality' ? 0.2 : 0}
            max={0.35}
            step={0.01}
            value={creativity}
            onChange={(e) => onCreativityChange(Number(e.target.value))}
            className="flex-1"
            disabled={busy}
            aria-label="Upscale creativity"
          />
          <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
            {creativity.toFixed(2)}
          </span>
        </label>
      )}

      {/* Tier 2 — factor picker (Recraft only). Stability fast is fixed
          at 4×; Stability HQ + reimagine size the output by megapixel
          target rather than a user-specified factor. */}
      {showFactor && (
        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Factor</span>
          <div
            className="grid grid-cols-2 gap-1 rounded-md border border-border p-0.5 bg-background"
            role="radiogroup"
            aria-label="Upscale factor"
          >
            {([2, 4] as UpscaleFactor[]).map((f) => (
              <button
                key={f}
                type="button"
                role="radio"
                aria-checked={factor === f}
                disabled={busy}
                className={`px-2 py-1 text-xs rounded ${
                  factor === f
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent'
                } disabled:opacity-50 disabled:pointer-events-none`}
                onClick={() => onFactorChange(f)}
              >
                {f}×
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tier 1 — output mode toggle. Same control pattern as I2I /
          Replace BG. Default is non-destructive new-layer at the
          state level. */}
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">Output</span>
        <div
          className="grid grid-cols-2 gap-1 rounded-md border border-border p-0.5 bg-background"
          role="radiogroup"
          aria-label="Upscale output mode"
        >
          <button
            type="button"
            role="radio"
            aria-checked={outputMode === 'replace'}
            disabled={busy}
            className={`px-2 py-1 text-xs rounded ${
              outputMode === 'replace'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            } disabled:opacity-50 disabled:pointer-events-none`}
            onClick={() => onOutputModeChange('replace')}
          >
            Replace layer
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={outputMode === 'new-layer'}
            disabled={busy}
            className={`px-2 py-1 text-xs rounded ${
              outputMode === 'new-layer'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            } disabled:opacity-50 disabled:pointer-events-none`}
            onClick={() => onOutputModeChange('new-layer')}
          >
            New layer
          </button>
        </div>
      </div>

      {/* Tier 3 — seed. Same UX as I2I / S&R. Only Stability HQ +
          reimagine respect it; hidden everywhere else. */}
      {showSeed && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-16">Seed</span>
          <input
            type="number"
            min={0}
            max={4_294_967_295}
            step={1}
            value={seed ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                onSeedChange(null);
                return;
              }
              const parsed = Number(raw);
              if (Number.isFinite(parsed) && parsed >= 0) {
                onSeedChange(Math.floor(parsed));
              }
            }}
            placeholder="random"
            className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
            disabled={busy}
            aria-label="Upscale seed"
          />
        </label>
      )}

      {/* Projected output dims — only meaningful when we know the
          source size, which depends on the active layer. */}
      {dims && (
        <p className="text-[11px] text-muted-foreground leading-snug tabular-nums">{dims}</p>
      )}

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onUpscale}
        disabled={!ready || busy}
      >
        Upscale (~{cost} credits)
      </button>
    </div>
  );
}

function VectorizeSubPanel({
  status,
  savedFileName,
  onVectorize,
  onSaveAgain,
  busy,
}: {
  status: 'idle' | 'saved' | 'cancelled';
  savedFileName: string | null;
  onVectorize: () => void;
  onSaveAgain: () => void;
  busy: boolean;
}) {
  // Vectorize is treated as an EXPORT operation, not an edit: the
  // editor canvas isn't modified. The user clicks Vectorize, the
  // route returns SVG text, and the OS save-as dialog opens for the
  // user to choose a location. After the dialog resolves, the panel
  // shifts state:
  //   • saved     — confirmation message + Save again button (re-opens
  //                 the dialog with the same SVG, no re-API call)
  //   • cancelled — gentle nudge ("you cancelled the dialog"); the
  //                 SVG stays in memory so Save again still works.
  //                 This recovers the user from accidentally hitting
  //                 Cancel on the OS dialog without paying twice.
  //   • idle      — fresh state, only the Vectorize button is shown.
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Convert this image to a scalable vector (SVG) and save it as a file. Best for logos, line
        art, and illustrations; photographs become noisy.
      </p>

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onVectorize}
        disabled={busy}
      >
        Vectorize (~1 credit)
      </button>

      {status === 'saved' && savedFileName && (
        <div className="space-y-1">
          <p className="text-[11px] text-muted-foreground leading-snug">
            Saved as <span className="font-mono">{savedFileName}</span>.
          </p>
          <button
            type="button"
            className="w-full px-3 py-1 text-[11px] rounded-md border border-border text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
            onClick={onSaveAgain}
            disabled={busy}
          >
            Save again
          </button>
        </div>
      )}

      {status === 'cancelled' && (
        <div className="space-y-1">
          <p className="text-[11px] text-muted-foreground leading-snug">
            Save cancelled. The SVG is still here — click below to save without re-running.
          </p>
          <button
            type="button"
            className="w-full px-3 py-1 text-[11px] rounded-md border border-border text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
            onClick={onSaveAgain}
            disabled={busy}
          >
            Save SVG
          </button>
        </div>
      )}
    </div>
  );
}

function ControlSubPanel({
  mode,
  onModeChange,
  prompt,
  onPromptChange,
  negativePrompt,
  onNegativePromptChange,
  controlStrength,
  onControlStrengthChange,
  fidelity,
  onFidelityChange,
  styleReferenceBlob,
  onStyleReferenceBlobChange,
  styleStrength,
  onStyleStrengthChange,
  compositionFidelity,
  onCompositionFidelityChange,
  changeStrength,
  onChangeStrengthChange,
  seed,
  onSeedChange,
  outputMode,
  onOutputModeChange,
  onControl,
  busy,
}: {
  mode: ControlMode;
  onModeChange: (mode: ControlMode) => void;
  prompt: string;
  onPromptChange: (next: string) => void;
  negativePrompt: string;
  onNegativePromptChange: (next: string) => void;
  controlStrength: number;
  onControlStrengthChange: (n: number) => void;
  fidelity: number;
  onFidelityChange: (n: number) => void;
  styleReferenceBlob: Blob | null;
  onStyleReferenceBlobChange: (blob: Blob | null) => void;
  styleStrength: number;
  onStyleStrengthChange: (n: number) => void;
  compositionFidelity: number;
  onCompositionFidelityChange: (n: number) => void;
  changeStrength: number;
  onChangeStrengthChange: (n: number) => void;
  seed: number | null;
  onSeedChange: (seed: number | null) => void;
  outputMode: EditorOutputMode;
  onOutputModeChange: (mode: EditorOutputMode) => void;
  onControl: () => void;
  busy: boolean;
}) {
  // Mode-first design — four buttons surface the cost-vs-capability
  // tradeoff up front. Each mode has different required inputs and
  // different sliders, so most of the panel below the mode toggle is
  // mode-conditional.
  //
  //   • Sketch / Structure: prompt + control strength
  //   • Match Style:        prompt + fidelity (active layer IS style)
  //   • Style Transfer:     style reference image + 3 sliders
  //                         (no prompt — the API doesn't accept one)
  const acceptsPrompt = mode !== 'style-transfer';
  const showSketchOrStructureStrength = mode === 'from-sketch' || mode === 'match-structure';
  const showFidelity = mode === 'match-style';
  const showStyleTransferControls = mode === 'style-transfer';
  const cost = CONTROL_COST_CREDITS[mode];

  // Action button gating — sketch/structure/match-style need a prompt;
  // style-transfer needs a reference image instead. (`cost` is read by
  // tests but no longer surfaced on the action button — the per-mode
  // cost lives on the mode toggle above, no need to repeat it.)
  const ready = acceptsPrompt ? prompt.trim().length > 0 : styleReferenceBlob !== null;
  void cost;

  return (
    <div className="space-y-2">
      {/* Tier 1 — mode toggle. Per-mode credit cost on each button so
          the user can compare without opening any sub-controls. The
          4-up grid puts the heavy tier (Style Transfer 10 cr) on the
          right where it's least likely to be hit by accident. */}
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">Mode</span>
        <div
          className="grid grid-cols-2 gap-1 rounded-md border border-border p-0.5 bg-background"
          role="radiogroup"
          aria-label="Control mode"
        >
          {(
            ['from-sketch', 'match-structure', 'match-style', 'style-transfer'] as ControlMode[]
          ).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              disabled={busy}
              className={`px-1.5 py-1 text-[11px] rounded ${
                mode === m
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent'
              } disabled:opacity-50 disabled:pointer-events-none flex flex-col items-center leading-tight`}
              onClick={() => onModeChange(m)}
            >
              <span className="text-xs">{CONTROL_MODE_LABEL[m]}</span>
              <span className="text-[10px] opacity-80">{CONTROL_COST_CREDITS[m]} cr</span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          {CONTROL_MODE_DESCRIPTION[mode]}
        </p>
      </div>

      {/* Tier 1 — prompt textarea (sketch / structure / match-style). */}
      {acceptsPrompt && (
        <label className="text-xs block">
          <span className="text-xs text-muted-foreground">Prompt</span>
          <textarea
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder={
              mode === 'from-sketch'
                ? 'Describe the polished image…'
                : mode === 'match-structure'
                  ? 'Describe the new content (same shape)…'
                  : 'Describe the new image (in this layer’s style)…'
            }
            rows={3}
            className="mt-1 w-full text-xs bg-background border border-border rounded-md px-2 py-1.5 resize-none"
            disabled={busy}
            aria-label="Control prompt"
          />
        </label>
      )}

      {/* Tier 1 — style reference (style-transfer only). Drop-zone +
          file picker, same pattern Replace BG uses for its background
          reference. Required for the action button to enable. */}
      {showStyleTransferControls && (
        <ReferenceImageDrop
          value={styleReferenceBlob}
          onChange={onStyleReferenceBlobChange}
          label="Style reference"
          hint="The output keeps your active layer’s content with this image’s aesthetic."
          disabled={busy}
        />
      )}

      {/* Tier 1 — output mode. Same toggle pattern as I2I / Replace BG /
          Upscale; default 'new-layer' sits at the EditorPage state level. */}
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">Output</span>
        <div
          className="grid grid-cols-2 gap-1 rounded-md border border-border p-0.5 bg-background"
          role="radiogroup"
          aria-label="Control output mode"
        >
          <button
            type="button"
            role="radio"
            aria-checked={outputMode === 'replace'}
            disabled={busy}
            className={`px-2 py-1 text-xs rounded ${
              outputMode === 'replace'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            } disabled:opacity-50 disabled:pointer-events-none`}
            onClick={() => onOutputModeChange('replace')}
          >
            Replace layer
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={outputMode === 'new-layer'}
            disabled={busy}
            className={`px-2 py-1 text-xs rounded ${
              outputMode === 'new-layer'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            } disabled:opacity-50 disabled:pointer-events-none`}
            onClick={() => onOutputModeChange('new-layer')}
          >
            New layer
          </button>
        </div>
      </div>

      {/* Tier 2 — control strength (sketch / structure). Higher = stick
          closer to the source's shape. Stability's docs cap at 1.0
          but recommend 0.5–0.8 for most cases. */}
      {showSketchOrStructureStrength && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-24">Control strength</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={controlStrength}
            onChange={(e) => onControlStrengthChange(Number(e.target.value))}
            className="flex-1"
            disabled={busy}
            aria-label="Control strength"
          />
          <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
            {controlStrength.toFixed(2)}
          </span>
        </label>
      )}

      {/* Tier 2 — fidelity (match-style). Higher = stronger style
          adherence. */}
      {showFidelity && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-24">Fidelity</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={fidelity}
            onChange={(e) => onFidelityChange(Number(e.target.value))}
            className="flex-1"
            disabled={busy}
            aria-label="Style fidelity"
          />
          <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
            {fidelity.toFixed(2)}
          </span>
        </label>
      )}

      {/* Tier 2 — style-transfer's three sliders. Stability documents
          these as independent axes:
            • styleStrength       — how much of the style image's look
                                    to apply
            • compositionFidelity — how strongly the content image's
                                    composition is preserved
            • changeStrength      — overall transformation intensity */}
      {showStyleTransferControls && (
        <>
          <label className="flex items-center gap-2">
            <span className="text-xs w-28">Style strength</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={styleStrength}
              onChange={(e) => onStyleStrengthChange(Number(e.target.value))}
              className="flex-1"
              disabled={busy}
              aria-label="Style strength"
            />
            <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
              {styleStrength.toFixed(2)}
            </span>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-xs w-28">Composition</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={compositionFidelity}
              onChange={(e) => onCompositionFidelityChange(Number(e.target.value))}
              className="flex-1"
              disabled={busy}
              aria-label="Composition fidelity"
            />
            <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
              {compositionFidelity.toFixed(2)}
            </span>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-xs w-28">Change strength</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={changeStrength}
              onChange={(e) => onChangeStrengthChange(Number(e.target.value))}
              className="flex-1"
              disabled={busy}
              aria-label="Change strength"
            />
            <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
              {changeStrength.toFixed(2)}
            </span>
          </label>
        </>
      )}

      {/* Tier 3 — negative prompt (all modes accept it via Stability's
          common-options bundle). */}
      <input
        type="text"
        value={negativePrompt}
        onChange={(e) => onNegativePromptChange(e.target.value)}
        placeholder="Negative prompt — what to avoid (optional)"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Control negative prompt"
      />

      {/* Tier 3 — seed. Same UX as I2I / Upscale / etc. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Seed</span>
        <input
          type="number"
          min={0}
          max={4_294_967_295}
          step={1}
          value={seed ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onSeedChange(null);
              return;
            }
            const parsed = Number(raw);
            if (Number.isFinite(parsed) && parsed >= 0) {
              onSeedChange(Math.floor(parsed));
            }
          }}
          placeholder="random"
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Control seed"
        />
      </label>

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onControl}
        disabled={!ready || busy}
      >
        {CONTROL_MODE_LABEL[mode]}
      </button>
    </div>
  );
}

/** Placeholder for capability sub-panels that haven't been wired up yet.
 *  The entity layer is in place (Stability + Recraft PRs landed on main),
 *  but the route + UI for these capabilities lands in follow-up commits
 *  on this branch. */
function SearchReplaceSubPanel({
  searchPrompt,
  onSearchPromptChange,
  replacePrompt,
  onReplacePromptChange,
  negativePrompt,
  onNegativePromptChange,
  growMaskPx,
  onGrowMaskPxChange,
  stylePreset,
  onStylePresetChange,
  seed,
  onSeedChange,
  onSearchReplace,
  busy,
}: {
  searchPrompt: string;
  onSearchPromptChange: (next: string) => void;
  replacePrompt: string;
  onReplacePromptChange: (next: string) => void;
  negativePrompt: string;
  onNegativePromptChange: (next: string) => void;
  growMaskPx: number;
  onGrowMaskPxChange: (px: number) => void;
  stylePreset: string;
  onStylePresetChange: (preset: string) => void;
  seed: number | null;
  onSeedChange: (seed: number | null) => void;
  onSearchReplace: () => void;
  busy: boolean;
}) {
  // Stability auto-segments the search target; no marquee required.
  // Both Search and Replace prompts are required — the run button stays
  // disabled until both have content.
  const ready = searchPrompt.trim().length > 0 && replacePrompt.trim().length > 0;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Stability AI auto-segments the object you describe and replaces it with content matching the
        replace prompt. No marquee needed. ~{SEARCH_REPLACE_COST_CREDITS} credits per use.
      </p>

      {/* Search prompt — what to find. */}
      <input
        type="text"
        value={searchPrompt}
        onChange={(e) => onSearchPromptChange(e.target.value)}
        placeholder="Search — what's currently in the image (e.g. 'the red car')"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Search prompt"
      />

      {/* Replace prompt — what should appear. */}
      <input
        type="text"
        value={replacePrompt}
        onChange={(e) => onReplacePromptChange(e.target.value)}
        placeholder="Replace — what should appear (e.g. 'a blue truck')"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Replace prompt"
      />

      {/* Negative prompt — features to avoid. Stability supports it
          natively for search-and-replace. */}
      <input
        type="text"
        value={negativePrompt}
        onChange={(e) => onNegativePromptChange(e.target.value)}
        placeholder="Negative prompt — what to avoid (optional)"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Search & Replace negative prompt"
      />

      {/* Grow mask — Stability's `grow_mask`, helps when the
          auto-segmentation just misses an edge. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Grow mask</span>
        <input
          type="range"
          min={0}
          max={64}
          step={1}
          value={growMaskPx}
          onChange={(e) => onGrowMaskPxChange(Number(e.target.value))}
          className="flex-1"
          disabled={busy}
          aria-label="Search & Replace mask growth"
        />
        <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
          {growMaskPx} px
        </span>
      </label>

      {/* Optional Stability style preset — same 11 named presets the
          Inpaint tool exposes for Stability. Empty = engine default. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Style</span>
        <select
          value={stylePreset}
          onChange={(e) => onStylePresetChange(e.target.value)}
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Search & Replace style preset"
        >
          <option value="">(default)</option>
          {STABILITY_INPAINT_STYLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      {/* Optional seed for reproducibility. Blank = engine random;
          a positive integer pins the result so the same prompts
          generate the same output. The number-input is parsed lazily
          so an empty string clears the seed back to null. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Seed</span>
        <input
          type="number"
          min={0}
          max={4_294_967_295}
          step={1}
          value={seed ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onSeedChange(null);
              return;
            }
            const parsed = Number(raw);
            if (Number.isFinite(parsed) && parsed >= 0) {
              onSeedChange(Math.floor(parsed));
            }
          }}
          placeholder="random"
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Search & Replace seed"
        />
      </label>
      <p className="text-[11px] text-muted-foreground leading-snug">
        Pin a number to lock the result — same prompts + same seed produce the same image. Leave
        blank for a fresh random variation each run.
      </p>

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onSearchReplace}
        disabled={!ready || busy}
      >
        Search &amp; Replace
      </button>
    </div>
  );
}

function SearchRecolorSubPanel({
  selectPrompt,
  onSelectPromptChange,
  colorPrompt,
  onColorPromptChange,
  negativePrompt,
  onNegativePromptChange,
  growMaskPx,
  onGrowMaskPxChange,
  stylePreset,
  onStylePresetChange,
  seed,
  onSeedChange,
  onSearchRecolor,
  busy,
}: {
  selectPrompt: string;
  onSelectPromptChange: (next: string) => void;
  colorPrompt: string;
  onColorPromptChange: (next: string) => void;
  negativePrompt: string;
  onNegativePromptChange: (next: string) => void;
  growMaskPx: number;
  onGrowMaskPxChange: (px: number) => void;
  stylePreset: string;
  onStylePresetChange: (preset: string) => void;
  seed: number | null;
  onSeedChange: (seed: number | null) => void;
  onSearchRecolor: () => void;
  busy: boolean;
}) {
  // Mirrors SearchReplaceSubPanel; the only differences are the prompt
  // labels (Select / Recolor instead of Search / Replace), the cost
  // constant (Stability charges slightly more for recolor), and the
  // run button label. Shape preservation is implicit in Stability's
  // endpoint — nothing to expose in the UI for it.
  const ready = selectPrompt.trim().length > 0 && colorPrompt.trim().length > 0;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Stability AI auto-segments the object you describe and recolors it without changing its
        shape. No marquee needed. ~{SEARCH_RECOLOR_COST_CREDITS} credits per use.
      </p>

      {/* Select prompt — what to recolor. */}
      <input
        type="text"
        value={selectPrompt}
        onChange={(e) => onSelectPromptChange(e.target.value)}
        placeholder="Select — what to recolor (e.g. 'the car')"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Search & Recolor select prompt"
      />

      {/* Color/effect prompt — describes the new color or material. */}
      <input
        type="text"
        value={colorPrompt}
        onChange={(e) => onColorPromptChange(e.target.value)}
        placeholder="Recolor — the new color or effect (e.g. 'cobalt blue', 'matte black')"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Search & Recolor color prompt"
      />

      <input
        type="text"
        value={negativePrompt}
        onChange={(e) => onNegativePromptChange(e.target.value)}
        placeholder="Negative prompt — what to avoid (optional)"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Search & Recolor negative prompt"
      />

      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Grow mask</span>
        <input
          type="range"
          min={0}
          max={64}
          step={1}
          value={growMaskPx}
          onChange={(e) => onGrowMaskPxChange(Number(e.target.value))}
          className="flex-1"
          disabled={busy}
          aria-label="Search & Recolor mask growth"
        />
        <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
          {growMaskPx} px
        </span>
      </label>

      {/* Tier 3 — style preset (lower utility for recolor since shape is
          preserved, but harmless and consistent with Inpaint / S&R). */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Style</span>
        <select
          value={stylePreset}
          onChange={(e) => onStylePresetChange(e.target.value)}
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Search & Recolor style preset"
        >
          <option value="">(default)</option>
          {STABILITY_INPAINT_STYLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      {/* Tier 3 — seed for reproducibility. Same control as S&R. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Seed</span>
        <input
          type="number"
          min={0}
          max={4_294_967_295}
          step={1}
          value={seed ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onSeedChange(null);
              return;
            }
            const parsed = Number(raw);
            if (Number.isFinite(parsed) && parsed >= 0) {
              onSeedChange(Math.floor(parsed));
            }
          }}
          placeholder="random"
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Search & Recolor seed"
        />
      </label>
      <p className="text-[11px] text-muted-foreground leading-snug">
        Pin a number to lock the result — same prompts + same seed produce the same image. Leave
        blank for a fresh random variation each run.
      </p>

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onSearchRecolor}
        disabled={!ready || busy}
      >
        Search &amp; Recolor
      </button>
    </div>
  );
}

function OutpaintSubPanel({
  leftPx,
  onLeftPxChange,
  rightPx,
  onRightPxChange,
  upPx,
  onUpPxChange,
  downPx,
  onDownPxChange,
  prompt,
  onPromptChange,
  creativity,
  onCreativityChange,
  negativePrompt,
  onNegativePromptChange,
  stylePreset,
  onStylePresetChange,
  seed,
  onSeedChange,
  onOutpaint,
  busy,
}: {
  leftPx: number;
  onLeftPxChange: (px: number) => void;
  rightPx: number;
  onRightPxChange: (px: number) => void;
  upPx: number;
  onUpPxChange: (px: number) => void;
  downPx: number;
  onDownPxChange: (px: number) => void;
  prompt: string;
  onPromptChange: (next: string) => void;
  creativity: number;
  onCreativityChange: (next: number) => void;
  negativePrompt: string;
  onNegativePromptChange: (next: string) => void;
  stylePreset: string;
  onStylePresetChange: (preset: string) => void;
  seed: number | null;
  onSeedChange: (seed: number | null) => void;
  onOutpaint: () => void;
  busy: boolean;
}) {
  // Outpaint extends the canvas — at least one of L/R/U/D must be > 0.
  // Stability auto-fills the new pixels; the optional prompt nudges
  // what shows up there. Creativity 0..1 controls adherence to the
  // source content (default 0.5 splits the difference).
  const totalPx = leftPx + rightPx + upPx + downPx;
  const ready = totalPx > 0;

  const clampEdge = (raw: string): number => {
    if (raw === '') return 0;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.min(OUTPAINT_MAX_EDGE_PX, Math.floor(n));
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Stability AI extends the image past its current edges. Set how many pixels to add on each
        side (max {OUTPAINT_MAX_EDGE_PX} per edge). ~{OUTPAINT_COST_CREDITS} credits per use.
      </p>

      {/* Tier 1 — directional pixel inputs in a 2×2 grid that mimics the
          spatial layout (Up on top, Left/Right in the middle, Down on
          the bottom). Easier to reason about than a flat list. */}
      <div className="grid grid-cols-3 gap-1.5 items-center">
        <div />
        <label className="flex flex-col items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Up</span>
          <input
            type="number"
            min={0}
            max={OUTPAINT_MAX_EDGE_PX}
            step={1}
            value={upPx}
            onChange={(e) => onUpPxChange(clampEdge(e.target.value))}
            className="w-full text-xs bg-background border border-border rounded-md px-2 py-1 text-center"
            disabled={busy}
            aria-label="Outpaint up pixels"
          />
        </label>
        <div />
        <label className="flex flex-col items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Left</span>
          <input
            type="number"
            min={0}
            max={OUTPAINT_MAX_EDGE_PX}
            step={1}
            value={leftPx}
            onChange={(e) => onLeftPxChange(clampEdge(e.target.value))}
            className="w-full text-xs bg-background border border-border rounded-md px-2 py-1 text-center"
            disabled={busy}
            aria-label="Outpaint left pixels"
          />
        </label>
        <div className="flex items-center justify-center">
          <span className="text-[11px] text-muted-foreground" aria-hidden="true">
            +
          </span>
        </div>
        <label className="flex flex-col items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Right</span>
          <input
            type="number"
            min={0}
            max={OUTPAINT_MAX_EDGE_PX}
            step={1}
            value={rightPx}
            onChange={(e) => onRightPxChange(clampEdge(e.target.value))}
            className="w-full text-xs bg-background border border-border rounded-md px-2 py-1 text-center"
            disabled={busy}
            aria-label="Outpaint right pixels"
          />
        </label>
        <div />
        <label className="flex flex-col items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Down</span>
          <input
            type="number"
            min={0}
            max={OUTPAINT_MAX_EDGE_PX}
            step={1}
            value={downPx}
            onChange={(e) => onDownPxChange(clampEdge(e.target.value))}
            className="w-full text-xs bg-background border border-border rounded-md px-2 py-1 text-center"
            disabled={busy}
            aria-label="Outpaint down pixels"
          />
        </label>
        <div />
      </div>

      {/* Optional prompt — guides what fills the new area. Skippable; the
          model can extend literal scenery on its own. */}
      <input
        type="text"
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        placeholder="Optional prompt — what should appear in the new area"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Outpaint prompt"
      />

      {/* Tier 2 — creativity slider. 0 = literal extension; 1 = invent
          freely. Default 0.5 mirrors Stability's own default. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Creativity</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={creativity}
          onChange={(e) => onCreativityChange(Number(e.target.value))}
          className="flex-1"
          disabled={busy}
          aria-label="Outpaint creativity"
        />
        <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
          {creativity.toFixed(2)}
        </span>
      </label>

      {/* Tier 2 — negative prompt. Same shape as other tools. */}
      <input
        type="text"
        value={negativePrompt}
        onChange={(e) => onNegativePromptChange(e.target.value)}
        placeholder="Negative prompt — what to avoid (optional)"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Outpaint negative prompt"
      />

      {/* Tier 3 — style preset (reuses the 11 Stability presets). */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Style</span>
        <select
          value={stylePreset}
          onChange={(e) => onStylePresetChange(e.target.value)}
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Outpaint style preset"
        >
          <option value="">(default)</option>
          {STABILITY_INPAINT_STYLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      {/* Tier 3 — seed. Same control as S&R / Search & Recolor. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Seed</span>
        <input
          type="number"
          min={0}
          max={4_294_967_295}
          step={1}
          value={seed ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onSeedChange(null);
              return;
            }
            const parsed = Number(raw);
            if (Number.isFinite(parsed) && parsed >= 0) {
              onSeedChange(Math.floor(parsed));
            }
          }}
          placeholder="random"
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Outpaint seed"
        />
      </label>
      <p className="text-[11px] text-muted-foreground leading-snug">
        Pin a number to lock the result — same prompts + same seed produce the same image. Leave
        blank for a fresh random variation each run.
      </p>

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onOutpaint}
        disabled={!ready || busy}
      >
        Outpaint
      </button>
    </div>
  );
}

function ImageToImageSubPanel({
  prompt,
  onPromptChange,
  engine,
  onEngineChange,
  strength,
  onStrengthChange,
  negativePrompt,
  onNegativePromptChange,
  stylePreset,
  onStylePresetChange,
  seed,
  onSeedChange,
  mode,
  onModeChange,
  onImageToImage,
  busy,
}: {
  prompt: string;
  onPromptChange: (next: string) => void;
  engine: ImageToImageEngine;
  onEngineChange: (engine: ImageToImageEngine) => void;
  strength: number;
  onStrengthChange: (next: number) => void;
  negativePrompt: string;
  onNegativePromptChange: (next: string) => void;
  stylePreset: string;
  onStylePresetChange: (preset: string) => void;
  seed: number | null;
  onSeedChange: (seed: number | null) => void;
  mode: EditorOutputMode;
  onModeChange: (mode: EditorOutputMode) => void;
  onImageToImage: () => void;
  busy: boolean;
}) {
  // Image to Image redraws the active layer guided by both the source
  // pixels and the prompt. Strength controls how much it deviates:
  // 0 = identical to source (no point), 1 = ignore source (== text-
  // to-image). 0.35 default keeps the source recognizable while
  // letting the prompt move the result.
  //
  // Style preset is Stability-Ultra-only — Core and SD3.5 ignore the
  // field server-side. We let users pick on any engine so a preference
  // set on Core carries over to Ultra; an inline note flags when the
  // current engine drops the value.
  const ready = prompt.trim().length > 0;
  const cost = I2I_ENGINE_COST_CREDITS[engine];
  const stylePresetSupported = engine === 'ultra';

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Stability AI redraws the image guided by the prompt + a strength slider that controls how
        much to deviate from the source. ~{cost} credits per use.
      </p>

      {/* Tier 1 — engine. Per-engine cost shown inline so the user
          can pick speed vs quality without leaving the panel. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Engine</span>
        <select
          value={engine}
          onChange={(e) => onEngineChange(e.target.value as ImageToImageEngine)}
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Image to Image engine"
        >
          {(Object.keys(I2I_ENGINE_LABELS) as ImageToImageEngine[]).map((key) => (
            <option key={key} value={key}>
              {I2I_ENGINE_LABELS[key]} (~{I2I_ENGINE_COST_CREDITS[key]} cr)
            </option>
          ))}
        </select>
      </label>

      {/* Multi-line + a visible header so this primary input is hard to
          confuse with the single-line "Negative prompt" box below — the
          two used to look identical and users would type into the wrong
          one and wonder why Reimagine stayed disabled. */}
      <label className="text-xs block">
        <span className="text-xs text-muted-foreground">Prompt</span>
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder="Describe the redrawn image…"
          rows={3}
          className="mt-1 w-full text-xs bg-background border border-border rounded-md px-2 py-1.5 resize-none"
          disabled={busy}
          aria-label="Image to Image prompt"
        />
      </label>

      {/* Tier 1 — strength. Stability convention: 0 = identical to
          source (no transformation), 1 = ignore source entirely. The
          sweet spot is usually 0.25–0.55 — beyond that the prompt
          dominates and the source is barely recognizable. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Strength</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={strength}
          onChange={(e) => onStrengthChange(Number(e.target.value))}
          className="flex-1"
          disabled={busy}
          aria-label="Image to Image strength"
        />
        <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
          {strength.toFixed(2)}
        </span>
      </label>
      <p className="text-[11px] text-muted-foreground leading-snug">
        0 = keep source unchanged · 1 = ignore source. Try 0.25–0.55 for most edits.
      </p>

      {/* Tier 2 — negative prompt. */}
      <input
        type="text"
        value={negativePrompt}
        onChange={(e) => onNegativePromptChange(e.target.value)}
        placeholder="Negative prompt — what to avoid (optional)"
        className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5"
        disabled={busy}
        aria-label="Image to Image negative prompt"
      />

      {/* Tier 3 — style preset. Only respected by Stability Ultra, but
          enabled on every engine so the user's choice persists when
          they switch engines. The note below the dropdown calls out
          that the value is dropped on Core / SD3.5. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Style</span>
        <select
          value={stylePreset}
          onChange={(e) => onStylePresetChange(e.target.value)}
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Image to Image style preset"
        >
          <option value="">(default)</option>
          {STABILITY_INPAINT_STYLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      {!stylePresetSupported && stylePreset !== '' && (
        <p className="text-[11px] text-muted-foreground leading-snug">
          Style presets only apply on Stability Ultra — your selection will be ignored on the
          current engine.
        </p>
      )}

      {/* Tier 3 — seed. */}
      <label className="flex items-center gap-2">
        <span className="text-xs w-16">Seed</span>
        <input
          type="number"
          min={0}
          max={4_294_967_295}
          step={1}
          value={seed ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onSeedChange(null);
              return;
            }
            const parsed = Number(raw);
            if (Number.isFinite(parsed) && parsed >= 0) {
              onSeedChange(Math.floor(parsed));
            }
          }}
          placeholder="random"
          className="flex-1 text-xs bg-background border border-border rounded-md px-2 py-1"
          disabled={busy}
          aria-label="Image to Image seed"
        />
      </label>
      <p className="text-[11px] text-muted-foreground leading-snug">
        Pin a number to lock the result — same prompt + same seed produce the same image. Leave
        blank for a fresh random variation each run.
      </p>

      {/* Output mode — segmented toggle. `Replace layer` bakes the
          result into the active layer's pixels (destructive, layer
          count constant); `New layer` adds the result on top as an
          editable layer aligned to the source (non-destructive, easy
          to compare/discard). The two-button layout mirrors the
          Smart Create blend/insert switch. */}
      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">Output</span>
        <div
          className="grid grid-cols-2 gap-1 rounded-md border border-border p-0.5 bg-background"
          role="radiogroup"
          aria-label="Image to Image output mode"
        >
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'replace'}
            disabled={busy}
            className={`px-2 py-1 text-xs rounded ${
              mode === 'replace'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            } disabled:opacity-50 disabled:pointer-events-none`}
            onClick={() => onModeChange('replace')}
          >
            Replace layer
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'new-layer'}
            disabled={busy}
            className={`px-2 py-1 text-xs rounded ${
              mode === 'new-layer'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent'
            } disabled:opacity-50 disabled:pointer-events-none`}
            onClick={() => onModeChange('new-layer')}
          >
            New layer
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-snug">
          {mode === 'replace'
            ? 'Bakes the redraw into the active layer — destructive.'
            : 'Drops the redraw on top as an editable layer — easy to compare or discard.'}
        </p>
      </div>

      <button
        type="button"
        className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
        onClick={onImageToImage}
        disabled={!ready || busy}
      >
        Reimagine
      </button>
    </div>
  );
}

function FillPanel({
  settings,
  onChange,
}: {
  settings: BrushSettings;
  onChange: (s: Partial<BrushSettings>) => void;
}) {
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Fill</h4>
      <label className="flex items-start gap-2">
        <span className="text-xs w-14 pt-2">Color</span>
        <ColorSwatchPopover
          value={{ type: 'solid', color: settings.color }}
          onChange={(v) => v.type === 'solid' && onChange({ color: v.color })}
          allowGradient={false}
          label="Fill color"
        />
      </label>
    </div>
  );
}

// Tier 2 Group B (2026-05-13): TextPanel extracted to its own file
// at components/TextPanel.tsx so the styling additions (tracking,
// leading, stroke, drop shadow, Google Fonts picker) don't grow
// this already 5.5k-line module further.

function ShapePanel({
  settings,
  onChange,
}: {
  settings: ShapeSettings;
  onChange: (s: Partial<ShapeSettings>) => void;
}) {
  const shapes: { type: ShapeType; label: string }[] = [
    { type: 'rect', label: 'Rectangle' },
    { type: 'circle', label: 'Circle' },
    { type: 'triangle', label: 'Triangle' },
    { type: 'line', label: 'Line' },
    // Tier 2 Group A vector primitives — these create vector
    // layers (editable in Path Edit mode) rather than Fabric
    // primitives. See `lib/shape-generators.ts`.
    { type: 'polygon', label: 'Polygon' },
    { type: 'star', label: 'Star' },
    { type: 'arrow', label: 'Arrow' },
  ];

  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Shape</h4>
      <div className="flex gap-1 flex-wrap">
        {shapes.map((s) => (
          <button
            key={s.type}
            type="button"
            className={`px-2 py-1 text-xs rounded ${settings.type === s.type ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
            onClick={() => onChange({ type: s.type })}
          >
            {s.label}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Fill</span>
        <ColorSwatchPopover
          value={shapeFillToColorValue(settings.fill)}
          onChange={(v) => onChange({ fill: colorValueToShapeFill(v) })}
          allowGradient
          label="Shape fill"
        />
        <button
          type="button"
          className={`text-xs px-2 py-0.5 rounded ${settings.fill === 'transparent' ? 'bg-primary text-primary-foreground' : 'bg-accent'}`}
          onClick={() =>
            onChange({ fill: settings.fill === 'transparent' ? '#ffffff' : 'transparent' })
          }
        >
          {settings.fill === 'transparent' ? 'No fill' : 'Clear'}
        </button>
      </label>
      <label className="flex items-start gap-2">
        <span className="text-xs w-14 pt-2">Stroke</span>
        <ColorSwatchPopover
          value={{ type: 'solid', color: settings.stroke }}
          onChange={(v) => v.type === 'solid' && onChange({ stroke: v.color })}
          allowGradient={false}
          label="Shape stroke"
        />
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-14">Width</span>
        <input
          type="range"
          min={0}
          max={20}
          value={settings.strokeWidth}
          onChange={(e) => onChange({ strokeWidth: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-8 text-right tabular-nums">{settings.strokeWidth}</span>
      </label>

      {settings.type === 'polygon' && (
        <label className="flex items-center gap-2">
          <span className="text-xs w-14">Sides</span>
          <input
            type="range"
            min={3}
            max={32}
            value={settings.polygonSides ?? 6}
            onChange={(e) => onChange({ polygonSides: Number(e.target.value) })}
            className="flex-1"
          />
          <span className="text-xs w-8 text-right tabular-nums">{settings.polygonSides ?? 6}</span>
        </label>
      )}
      {settings.type === 'star' && (
        <>
          <label className="flex items-center gap-2">
            <span className="text-xs w-14">Points</span>
            <input
              type="range"
              min={3}
              max={32}
              value={settings.starPoints ?? 5}
              onChange={(e) => onChange({ starPoints: Number(e.target.value) })}
              className="flex-1"
            />
            <span className="text-xs w-8 text-right tabular-nums">{settings.starPoints ?? 5}</span>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-xs w-14">Inner</span>
            <input
              type="range"
              min={10}
              max={95}
              value={Math.round((settings.starInnerRadiusRatio ?? 0.5) * 100)}
              onChange={(e) => onChange({ starInnerRadiusRatio: Number(e.target.value) / 100 })}
              className="flex-1"
            />
            <span className="text-xs w-8 text-right tabular-nums">
              {Math.round((settings.starInnerRadiusRatio ?? 0.5) * 100)}%
            </span>
          </label>
        </>
      )}
      {settings.type === 'arrow' && (
        <>
          <label className="flex items-center gap-2">
            <span className="text-xs w-14">Head</span>
            <select
              value={settings.arrowHeadShape ?? 'triangle'}
              onChange={(e) =>
                onChange({
                  arrowHeadShape: e.target.value as 'triangle' | 'open' | 'diamond',
                })
              }
              className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs"
            >
              <option value="triangle">Triangle</option>
              <option value="open">Open chevron</option>
              <option value="diamond">Diamond</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="text-xs w-14">Head size</span>
            <input
              type="range"
              min={8}
              max={80}
              value={settings.arrowHeadSize ?? 24}
              onChange={(e) => onChange({ arrowHeadSize: Number(e.target.value) })}
              className="flex-1"
            />
            <span className="text-xs w-8 text-right tabular-nums">
              {settings.arrowHeadSize ?? 24}
            </span>
          </label>
        </>
      )}
    </div>
  );
}

// Inline SVGs for the rect/square/ellipse/circle marquee buttons. Lucide
// only ships `Square` and `Circle`; the wide-aspect Rectangle and Ellipse
// variants are drawn here so all four shape buttons stay visually consistent.
function RectangleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="6" width="20" height="12" rx="1" />
    </svg>
  );
}

function SquareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="4" width="16" height="16" rx="1" />
    </svg>
  );
}

function EllipseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <ellipse cx="12" cy="12" rx="10" ry="6" />
    </svg>
  );
}

function CircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function MarqueePanel({
  settings,
  onChange,
  hasSelection,
  canGrow,
  onGrowSelection,
  onInvertSelection,
  onSelectSubject,
  selectSubjectBusy,
  quickMaskActive,
  onToggleQuickMask,
  onOpenColorRange,
  onOpenRefineEdge,
  onActivateSlices,
}: {
  settings: MarqueeSettings;
  onChange: (s: Partial<MarqueeSettings>) => void;
  hasSelection: boolean;
  canGrow: boolean;
  onGrowSelection: (deltaPx: number) => void;
  onInvertSelection: () => void;
  onSelectSubject?: () => void;
  selectSubjectBusy?: boolean;
  quickMaskActive?: boolean;
  onToggleQuickMask?: () => void;
  onOpenColorRange?: () => void;
  onOpenRefineEdge?: () => void;
  /** Switch the editor into Slice mode. The Slice tool is hidden from
   *  the toolbar to save mobile space; this button is its replacement
   *  entry point. The full slice list + canvas handles take over via
   *  `SlicePanel` once `activeTool === 'slice'`. */
  onActivateSlices?: () => void;
}) {
  // Rectangle/Square share `shape: 'rect'` and differ only by forceSquare.
  // Ellipse/Circle share `shape: 'ellipse'`. Lasso/Polygon/Wand don't care
  // about forceSquare, so we always reset it to false to keep state clean.
  const buttons: {
    id: string;
    label: string;
    shape: MarqueeShape;
    forceSquare: boolean;
    icon: React.ComponentType<{ className?: string }>;
  }[] = [
    {
      id: 'rect-rect',
      label: 'Rectangular Selection',
      shape: 'rect',
      forceSquare: false,
      icon: RectangleIcon,
    },
    {
      id: 'rect-square',
      label: 'Square Selection',
      shape: 'rect',
      forceSquare: true,
      icon: SquareIcon,
    },
    {
      id: 'ellipse-ellipse',
      label: 'Elliptical Selection',
      shape: 'ellipse',
      forceSquare: false,
      icon: EllipseIcon,
    },
    {
      id: 'ellipse-circle',
      label: 'Circular Selection',
      shape: 'ellipse',
      forceSquare: true,
      icon: CircleIcon,
    },
    { id: 'lasso', label: 'Free Selection', shape: 'lasso', forceSquare: false, icon: Lasso },
    {
      id: 'polygon',
      label: 'Path Selection',
      shape: 'polygon',
      forceSquare: false,
      icon: Pentagon,
    },
    { id: 'wand', label: 'Pixel Selection', shape: 'wand', forceSquare: false, icon: Wand2 },
  ];

  const isWand = settings.shape === 'wand';

  const activeButton = buttons.find((b) => {
    if (b.shape !== settings.shape) return false;
    if (b.shape === 'rect' || b.shape === 'ellipse') {
      return b.forceSquare === settings.forceSquare;
    }
    return true;
  });
  const headerLabel = activeButton?.label ?? 'Selection';

  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">{headerLabel}</h4>
      <div className="flex gap-2 flex-wrap">
        {buttons.map((b) => {
          const Icon = b.icon;
          const isActive = activeButton?.id === b.id;
          return (
            <button
              key={b.id}
              type="button"
              title={b.label}
              className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-accent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => onChange({ shape: b.shape, forceSquare: b.forceSquare })}
            >
              <Icon className="h-5 w-5" />
            </button>
          );
        })}
        {/* AI Select Subject — sits with the marquee shape buttons
            because it CREATES a selection (like the others) rather
            than modifying one. It's a one-shot action, not a
            persistent mode, so it never renders as "active". */}
        {onSelectSubject && (
          <button
            type="button"
            title={
              selectSubjectBusy ? 'Selecting subject…' : 'AI: detect & select the main subject'
            }
            disabled={selectSubjectBusy}
            className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors border border-primary/40 ${
              selectSubjectBusy
                ? 'text-primary opacity-50 cursor-not-allowed'
                : 'text-primary hover:bg-primary/10'
            }`}
            onClick={onSelectSubject}
          >
            <Sparkles className="h-5 w-5" />
          </button>
        )}
      </div>
      {isWand && (
        <>
          <label className="flex items-center gap-2">
            <span className="text-xs w-24">Tolerance</span>
            <input
              type="range"
              min={0}
              max={255}
              value={settings.tolerance}
              onChange={(e) => onChange({ tolerance: Number(e.target.value) })}
              className="flex-1"
            />
            <span className="text-xs w-10 text-right tabular-nums">{settings.tolerance}</span>
          </label>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={settings.contiguous}
              onChange={(e) => onChange({ contiguous: e.target.checked })}
              className="w-4 h-4 cursor-pointer"
            />
            <span>Contiguous</span>
          </label>
        </>
      )}
      <label className="flex items-start gap-2">
        <span className="text-xs w-24 pt-2">Marquee Color</span>
        <ColorSwatchPopover
          value={{ type: 'solid', color: settings.color }}
          onChange={(v) => v.type === 'solid' && onChange({ color: v.color })}
          allowGradient={false}
          label="Marquee color"
        />
      </label>
      {/* Modify Selection — renders whenever there's an active
          selection. Invert always available; grow/shrink only when
          the active shape supports it (rect/ellipse/wand/lasso).
          Refine Edge sits here too because it refines an existing
          selection. */}
      {hasSelection && (
        <div className="pt-2 border-t border-border space-y-2">
          <h5 className="text-xs font-medium text-muted-foreground uppercase mb-2">
            Modify Selection
          </h5>
          <SelectionOpsPanel
            hasSelection
            canGrow={canGrow}
            onGrow={onGrowSelection}
            onInvert={onInvertSelection}
          />
          {onOpenRefineEdge && (
            <button
              type="button"
              onClick={onOpenRefineEdge}
              className="w-full px-3 py-1.5 rounded border border-border text-xs hover:bg-accent text-foreground"
            >
              Refine Edge…
            </button>
          )}
        </div>
      )}
      {/* Selection creators — Color Range and Quick Mask CREATE a
          selection, so they're available regardless of whether one
          exists. Slices live here too: not strictly a selection
          creator, but operationally a select-style mode (pick / move /
          resize rectangular regions) that we moved out of the toolbar
          to save mobile space. */}
      {(onOpenColorRange || onToggleQuickMask || onActivateSlices) && (
        <div className="pt-2 border-t border-border space-y-2">
          <h5 className="text-xs font-medium text-muted-foreground uppercase mb-2">Create</h5>
          {onOpenColorRange && (
            <button
              type="button"
              onClick={onOpenColorRange}
              className="w-full px-3 py-1.5 rounded border border-border text-xs hover:bg-accent text-foreground"
            >
              Color Range…
            </button>
          )}
          {onToggleQuickMask && (
            <button
              type="button"
              onClick={onToggleQuickMask}
              className={`w-full px-3 py-1.5 rounded text-xs ${
                quickMaskActive
                  ? 'bg-primary text-primary-foreground hover:opacity-90'
                  : 'border border-border hover:bg-accent text-foreground'
              }`}
            >
              {quickMaskActive ? 'Exit Quick Mask' : 'Quick Mask'}
            </button>
          )}
          {onActivateSlices && (
            <button
              type="button"
              onClick={onActivateSlices}
              className="w-full px-3 py-1.5 rounded border border-border text-xs hover:bg-accent text-foreground"
              title="Switch to Slice mode — view, add, move, and resize export slice regions."
            >
              Slices
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Quick Mask properties panel (Tier 1 Group D). Renders when
 *  `activeTool === 'quickmask'`. Surfaces:
 *   - A one-line description of the mode.
 *   - Paint / Erase mode toggle (Alt-key still inverts a single
 *     stroke, matching Photoshop).
 *   - Brush size + opacity (shared with the Draw tool's
 *     `brushSettings` so changes persist across modes).
 *   - "Output To" picker: Selection (Photoshop default) or Layer
 *     Mask (apply to the active layer as a clip mask).
 *   - "Apply" primary button — exits QM and converts the painted
 *     mask via the chosen output. "Cancel" discards without applying.
 *
 *  Brush color is ignored in QM (the alpha mask is white-on-clear);
 *  the swatch is intentionally NOT shown to avoid the user
 *  thinking they're picking a tint. */
export type QuickMaskOutput = 'selection' | 'mask';

function QuickMaskPanel({
  brush,
  onChangeBrush,
  mode,
  onChangeMode,
  onApply,
  onCancel,
  canApplyAsMask,
}: {
  brush: BrushSettings;
  onChangeBrush: (s: Partial<BrushSettings>) => void;
  mode: 'add' | 'subtract';
  onChangeMode: (mode: 'add' | 'subtract') => void;
  onApply: (output: QuickMaskOutput) => void;
  onCancel: () => void;
  /** False when the active layer can't host a mask (e.g. no active
   *  layer). The dropdown's "Layer Mask" option grays out and the
   *  default falls back to Selection. */
  canApplyAsMask: boolean;
}) {
  // Default to Layer Mask when the active layer can host one — the
  // common Quick Mask intent is "paint a mask on this layer."
  // Fall back to Selection only when there's no eligible layer.
  const [output, setOutput] = useState<QuickMaskOutput>(canApplyAsMask ? 'mask' : 'selection');
  // If we lose mask eligibility mid-session (active layer removed,
  // etc.), snap back to selection so the user doesn't try to apply
  // an invalid output.
  useEffect(() => {
    if (!canApplyAsMask && output === 'mask') setOutput('selection');
  }, [canApplyAsMask, output]);

  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Quick Mask</h4>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Paint to add to the selection; erase to remove. Hold Alt to temporarily invert your brush.
        Pick an output below, then <strong>Apply</strong>.
      </p>

      {/* Mode toggle — visual mirror of the brush behavior. */}
      <div>
        <div className="text-xs text-muted-foreground mb-1.5">Mode</div>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          <button
            type="button"
            onClick={() => onChangeMode('add')}
            className={`px-3 py-1.5 text-xs ${
              mode === 'add'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-foreground hover:bg-accent'
            }`}
          >
            Paint
          </button>
          <button
            type="button"
            onClick={() => onChangeMode('subtract')}
            className={`px-3 py-1.5 text-xs border-l border-border ${
              mode === 'subtract'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-foreground hover:bg-accent'
            }`}
          >
            Erase
          </button>
        </div>
      </div>

      <label className="flex items-center gap-2">
        <span className="text-xs w-24">Size</span>
        <input
          type="range"
          min={1}
          max={200}
          value={brush.width}
          onChange={(e) => onChangeBrush({ width: Number(e.target.value) })}
          className="flex-1"
        />
        <span className="text-xs w-10 text-right tabular-nums">{brush.width}</span>
      </label>
      <label className="flex items-center gap-2">
        <span className="text-xs w-24">Opacity</span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(brush.opacity * 100)}
          onChange={(e) => onChangeBrush({ opacity: Number(e.target.value) / 100 })}
          className="flex-1"
        />
        <span className="text-xs w-10 text-right tabular-nums">
          {Math.round(brush.opacity * 100)}%
        </span>
      </label>

      <label className="flex items-center gap-2">
        <span className="text-xs w-24">Output To</span>
        <select
          value={output}
          onChange={(e) => setOutput(e.target.value as QuickMaskOutput)}
          className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs"
        >
          <option value="selection">Selection</option>
          <option value="mask" disabled={!canApplyAsMask}>
            Layer Mask
            {!canApplyAsMask ? ' (active layer needed)' : ''}
          </option>
        </select>
      </label>

      <div className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-3 py-2 rounded-md border border-border text-sm hover:bg-accent"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onApply(output)}
          className="flex-1 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

/** Free Transform PropertiesPanel (Tier 1 Group E Phase 1). Active
 *  when `state.transformActive` is true; replaces whatever tool
 *  panel was rendering before. Surfaces:
 *   - Numeric inputs (X, Y, W, H, rotation, skew X/Y) live-synced
 *     to the underlying Fabric matrix via rAF polling.
 *   - Mode picker — Free / Skew (affine, Fabric-native) and
 *     Distort / Perspective / Warp (non-affine, custom rasterizer).
 *   - Lock ratio toggle for proportional resize.
 *   - Apply / Cancel buttons.
 *
 *  Live-sync via rAF rather than canvas-event subscription so the
 *  panel stays self-contained — no canvas-hook event wiring needed,
 *  and the cost (one getter call per frame while the panel is
 *  mounted) is negligible. */
/** Mode picker entries. All five modes ship in Phase 1: Free/Skew
 *  via Fabric's native affine handles; Distort, Perspective, Warp
 *  via the custom-overlay path (projective DLT + tessellated
 *  rasterizer for Distort/Perspective; 3×3 mesh + Catmull-Rom for
 *  Warp). See `lib/projective-transform.ts` and `lib/warp-mesh.ts`. */
const TRANSFORM_MODES: {
  value: 'free' | 'skew' | 'distort' | 'perspective' | 'warp';
  label: string;
}[] = [
  { value: 'free', label: 'Free' },
  { value: 'skew', label: 'Skew' },
  { value: 'distort', label: 'Distort' },
  { value: 'perspective', label: 'Perspective' },
  { value: 'warp', label: 'Warp' },
];

const WARP_PRESETS: { value: 'arc' | 'bulge' | 'twist' | 'wave'; label: string }[] = [
  { value: 'arc', label: 'Arc' },
  { value: 'bulge', label: 'Bulge' },
  { value: 'twist', label: 'Twist' },
  { value: 'wave', label: 'Wave' },
];

function FreeTransformPanel({
  mode,
  onChangeMode,
  getMatrix,
  onChangeMatrix,
  onApply,
  onCancel,
  onApplyWarpPreset,
}: {
  mode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp';
  onChangeMode: (mode: 'free' | 'skew' | 'distort' | 'perspective' | 'warp') => void;
  getMatrix: () => TransformMatrix | null;
  onChangeMatrix: (patch: Partial<TransformMatrix>) => void;
  onApply: () => void;
  onCancel: () => void;
  /** Apply a Warp preset (arc / bulge / twist / wave) to the
   *  active overlay. Rendered as a preset row when mode='warp'.
   *  Undefined hides the row (e.g. when canvas-side hook hasn't
   *  exposed the helper yet). */
  onApplyWarpPreset?: (preset: 'arc' | 'bulge' | 'twist' | 'wave') => void;
}) {
  const [matrix, setMatrix] = useState<TransformMatrix | null>(getMatrix);
  const [lockRatio, setLockRatio] = useState(false);

  // rAF poll — keep the panel in sync with on-canvas drags. Only
  // setState if the values actually changed to avoid useless re-
  // renders during idle. Cleaned up on unmount.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const m = getMatrix();
      if (m) {
        setMatrix((prev) => {
          if (
            prev &&
            prev.x === m.x &&
            prev.y === m.y &&
            prev.width === m.width &&
            prev.height === m.height &&
            prev.rotation === m.rotation &&
            prev.skewX === m.skewX &&
            prev.skewY === m.skewY
          ) {
            return prev;
          }
          return m;
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [getMatrix]);

  const aspect = matrix && matrix.height > 0 ? matrix.width / matrix.height : 1;

  /** Handle a numeric-field commit. Applied on blur or Enter,
   *  not on every keystroke — typing "1024" character-by-character
   *  would otherwise resize to "1", "10", "102", "1024" mid-typing
   *  which feels jittery and can scale text/shape geometry into
   *  weird intermediate sizes. */
  const commit = (patch: Partial<TransformMatrix>) => {
    let next = patch;
    if (lockRatio && (patch.width !== undefined || patch.height !== undefined) && aspect > 0) {
      if (patch.width !== undefined) {
        next = { ...next, height: patch.width / aspect };
      } else if (patch.height !== undefined) {
        next = { ...next, width: patch.height * aspect };
      }
    }
    onChangeMatrix(next);
  };

  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Free Transform</h4>

      {/* Numeric matrix readout. Display-only when a non-affine
          mode is active — Distort/Perspective/Warp don't decompose
          to scalar rotation + skew, so the numbers don't represent
          the actual transform there. */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <NumberField
          label="X"
          value={matrix?.x ?? 0}
          onCommit={(v) => commit({ x: v })}
          disabled={!matrix}
        />
        <NumberField
          label="Y"
          value={matrix?.y ?? 0}
          onCommit={(v) => commit({ y: v })}
          disabled={!matrix}
        />
        <NumberField
          label="W"
          value={matrix?.width ?? 0}
          onCommit={(v) => commit({ width: v })}
          disabled={!matrix || mode !== 'free'}
        />
        <NumberField
          label="H"
          value={matrix?.height ?? 0}
          onCommit={(v) => commit({ height: v })}
          disabled={!matrix || mode !== 'free'}
        />
        <NumberField
          label="°"
          value={matrix?.rotation ?? 0}
          onCommit={(v) => commit({ rotation: v })}
          disabled={!matrix || mode !== 'free'}
          step={1}
        />
        <label className="flex items-center gap-1 text-[11px]">
          <input
            type="checkbox"
            checked={lockRatio}
            onChange={(e) => setLockRatio(e.target.checked)}
            className="w-3.5 h-3.5"
          />
          Lock ratio
        </label>
        <NumberField
          label="Sk X"
          value={matrix?.skewX ?? 0}
          onCommit={(v) => commit({ skewX: v })}
          disabled={!matrix || (mode !== 'free' && mode !== 'skew')}
          step={1}
        />
        <NumberField
          label="Sk Y"
          value={matrix?.skewY ?? 0}
          onCommit={(v) => commit({ skewY: v })}
          disabled={!matrix || (mode !== 'free' && mode !== 'skew')}
          step={1}
        />
      </div>

      <div className="pt-2 border-t border-border">
        <div className="text-xs text-muted-foreground mb-1.5">Mode</div>
        <div className="grid grid-cols-3 gap-1">
          {TRANSFORM_MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => onChangeMode(m.value)}
              className={`px-2 py-1.5 text-[11px] rounded ${
                mode === m.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background border border-border hover:bg-accent text-foreground'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {(mode === 'distort' || mode === 'perspective' || mode === 'warp') && (
          <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
            {mode === 'distort'
              ? 'Drag the corner handles to freely deform the layer.'
              : mode === 'perspective'
                ? 'Drag a corner — the opposite corner mirrors symmetrically for 1-point perspective.'
                : 'Drag the 9 mesh nodes to warp the layer. Pick a preset below to seed common shapes.'}
          </p>
        )}
        {mode === 'warp' && onApplyWarpPreset && (
          <div className="mt-2">
            <div className="text-[11px] text-muted-foreground mb-1.5">Preset</div>
            <div className="grid grid-cols-4 gap-1">
              {WARP_PRESETS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => onApplyWarpPreset(p.value)}
                  className="px-2 py-1 text-[11px] rounded bg-background border border-border hover:bg-accent text-foreground"
                  title={`Seed the mesh with the ${p.label} shape`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-2 border-t border-border">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-3 py-2 rounded-md border border-border text-sm hover:bg-accent"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onApply}
          className="flex-1 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
        >
          Apply
        </button>
      </div>
    </div>
  );
}

/** Compact number input that commits on blur / Enter (not on every
 *  keystroke). The display string is local state so partial typing
 *  (e.g. "12" while heading toward "1234") doesn't fight the rAF
 *  poll. On commit, parses and forwards a finite number; ignores
 *  non-numeric input by reverting to the source value. */
function NumberField({
  label,
  value,
  onCommit,
  disabled,
  step = 1,
}: {
  label: string;
  value: number;
  onCommit: (next: number) => void;
  disabled?: boolean;
  step?: number;
}) {
  const [draft, setDraft] = useState(() => formatNum(value));
  const [editing, setEditing] = useState(false);
  // Sync the displayed value when the upstream changes AND we
  // aren't actively editing — preserves the user's partial input
  // while the rAF poll is updating from canvas drags.
  useEffect(() => {
    if (!editing) setDraft(formatNum(value));
  }, [value, editing]);
  return (
    <label className="flex items-center gap-1 text-[11px]">
      <span className="text-muted-foreground w-8 shrink-0">{label}</span>
      <input
        type="number"
        step={step}
        value={draft}
        disabled={disabled}
        onFocus={() => setEditing(true)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const n = parseFloat(draft);
          if (Number.isFinite(n)) onCommit(n);
          else setDraft(formatNum(value));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setDraft(formatNum(value));
            (e.currentTarget as HTMLInputElement).blur();
          }
        }}
        className="flex-1 min-w-0 bg-background border border-border rounded px-1.5 py-0.5 text-xs tabular-nums disabled:opacity-50"
      />
    </label>
  );
}

function formatNum(v: number): string {
  if (!Number.isFinite(v)) return '0';
  // 1 decimal max for compact display — Photoshop's panel does
  // the same; pixel coords are usually integral but skew degrees
  // benefit from one fractional digit.
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function CropPanel({ onApply }: { onApply: () => void }) {
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Crop</h4>
      <p className="text-xs text-muted-foreground">
        Drag the corners or sides of the rectangle to set the crop area.
      </p>
      <button
        type="button"
        className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
        onClick={onApply}
      >
        <Crop className="h-4 w-4" />
        Crop
      </button>
    </div>
  );
}

function MoveOptionsPanel({
  autoSelectLayers,
  onAutoSelectLayersChange,
}: {
  autoSelectLayers: boolean;
  onAutoSelectLayersChange: (value: boolean) => void;
}) {
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Options</h4>
      <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
        <input
          type="checkbox"
          className="h-3.5 w-3.5"
          checked={autoSelectLayers}
          onChange={(e) => onAutoSelectLayersChange(e.target.checked)}
        />
        <span className="text-foreground">Auto-Select</span>
        <span
          className="text-muted-foreground"
          title="On: clicking a layer on the canvas makes it active. Off: clicking anywhere drags the currently-active layer; switch via the Layers panel."
        >
          (click to switch)
        </span>
      </label>
    </div>
  );
}

function TransformPanel({
  onRotateLeft,
  onRotateRight,
  onFlipHorizontal,
  onFlipVertical,
  onOpenFreeTransform,
}: {
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onFlipHorizontal: () => void;
  onFlipVertical: () => void;
  /** Open Free Transform on the active layer (Tier 1 Group E
   *  Phase 1a). Undefined hides the button — EditorPage passes
   *  undefined when there is no active layer to transform. */
  onOpenFreeTransform?: () => void;
}) {
  const buttons: {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    onClick: () => void;
  }[] = [
    { id: 'rot-left', label: 'Rotate left 90°', icon: RotateCcw, onClick: onRotateLeft },
    { id: 'rot-right', label: 'Rotate right 90°', icon: RotateCw, onClick: onRotateRight },
    { id: 'flip-h', label: 'Flip horizontal', icon: FlipHorizontal2, onClick: onFlipHorizontal },
    { id: 'flip-v', label: 'Flip vertical', icon: FlipVertical2, onClick: onFlipVertical },
  ];

  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Transform</h4>
      <div className="flex gap-2 flex-wrap">
        {buttons.map((b) => {
          const Icon = b.icon;
          return (
            <button
              key={b.id}
              type="button"
              title={b.label}
              className="w-10 h-10 flex items-center justify-center rounded-md bg-accent text-muted-foreground hover:text-foreground transition-colors"
              onClick={b.onClick}
            >
              <Icon className="h-5 w-5" />
            </button>
          );
        })}
      </div>
      {onOpenFreeTransform && (
        <button
          type="button"
          onClick={onOpenFreeTransform}
          className="w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent flex items-center justify-center gap-2"
          title="Free Transform on the active layer (Ctrl/Cmd+T)"
        >
          <Move3D className="h-3.5 w-3.5" />
          Free Transform…
        </button>
      )}
    </div>
  );
}

function FilterPanel({
  onOpenAdjustment,
  onOpenFilter,
  canAdjustActiveLayer,
}: {
  onOpenAdjustment: (kind: AdjustmentKind) => void;
  onOpenFilter: (kind: AdjustmentKind) => void;
  canAdjustActiveLayer: boolean;
}) {
  return (
    <div className={SECTION_CLASS}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase">Color &amp; Tone</h4>
      <div className="flex gap-2">
        <div className="flex-1">
          <AdjustmentsPicker onPick={onOpenAdjustment} disabled={!canAdjustActiveLayer} />
        </div>
        <div className="flex-1">
          <FilterPicker onPick={onOpenFilter} disabled={!canAdjustActiveLayer} />
        </div>
      </div>
    </div>
  );
}
